/**
 * FinanceTab.jsx - Finance Panel Component
 * 
 * Part 1 Checklist Items:
 * - A1: Panel Title Block ("Finance" + subtitle)
 * - A2: Add Finance Entry Button (gold button)
 * - A3: Button State Toggle (Cancel when form open)
 * - B1: KPI Strip Container (horizontal scrollable)
 * - B2: Total Allowed Cell
 * - B3: Total Spent Cell
 * - B4: Available Balance Cell
 * - B5: Amount Next Due Cell
 * - B6: Total Over Spent Cell (red if > 0)
 * - B7: Reserve (Locked) Cell (green)
 * - B8: KPI Cell Labels
 * - B9: KPI Cell Values (conditional colors)
 * - B10: KPI Dividers
 * - B11: Currency Formatting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Check, AlertCircle, AlertTriangle, DollarSign, Upload, FileText, Cloud, Search, ChevronDown, ChevronUp, Flag, Pause, RefreshCw, Star, MessageSquare, Trash2, Receipt, Download, Calendar, Copy, ExternalLink, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { theme } from '../../data/colors';
import AdminPageHeader from './AdminPageHeader';
import OrchestrationIndicator from './event-readiness/OrchestrationIndicator';
import { getModuleAwareness } from '../../data/api';
import { MiloNotebookItems } from './_shared/MiloNotebookItems';
import { isIntelligenceNotebookEnabled } from './intelligence-notebook/intelligenceNotebookFlag';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Color System (mapped to theme) ───────────────────────────────────────────────────────────────

const COLORS = {
  gold: theme.accentPrimary,
  goldLight: theme.accentTintLight,
  goldBorder: theme.borderMedium,
  white: theme.bgCard,
  surface: theme.bgCardWarm,
  grey: theme.textMuted,
  greyDark: theme.textSecondary,
  textPrimary: theme.textPrimary,
  textMuted: theme.textMuted,
  error: theme.statusError,
  errorLight: theme.statusErrorTint,
  success: theme.statusSuccess,
  successLight: theme.statusSuccess,
  successBg: theme.statusSuccessTint,
  warning: theme.statusWarning,
  warningLight: theme.statusWarningTint,
  warningBorder: 'rgba(245,158,11,0.25)',
  amber: '#D97706',
  amberLight: 'rgba(217,119,6,0.1)',
};

// ─── B11: Currency Formatting Helper ────────────────────────────────────────────

function fmt(value) {
  return `$${(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const CONTINGENCY_PCT = 0.15;

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // Container
  tabContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  
  // A1: Panel Header
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.goldBorder}`,
    background: theme.bgCard,
  },
  panelTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  panelTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.textPrimary,
    letterSpacing: '-0.3px',
  },
  panelSubtitle: {
    fontSize: 13,
    color: COLORS.textMuted,
    fontWeight: 500,
  },
  
  // A2 & A3: Add Entry Button
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 12,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    color: COLORS.white,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  
  // B1: KPI Strip Container
  kpiStrip: {
    padding: '16px 24px',
    background: theme.bgCard,
    borderBottom: `1px solid ${COLORS.goldBorder}`,
    overflowX: 'auto',
  },
  
  // Dashboard Grid Wrapper
  dashboardWrapper: {
    padding: '16px 24px',
    background: theme.bgCard,
  },
  
  kpiRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
  },
  
  // B2-B7: KPI Cells
  kpiCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    minWidth: 140,
    flex: 1,
  },
  
  // B8: KPI Cell Labels
  kpiCellLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: COLORS.grey,
    marginBottom: 6,
    whiteSpace: 'nowrap',
  },
  
  // B9: KPI Cell Values
  kpiCellValue: {
    fontSize: 26,
    fontWeight: 700,
    color: COLORS.textPrimary,
    letterSpacing: '-0.5px',
  },
  
  // B10: KPI Dividers
  kpiDivider: {
    width: 1,
    alignSelf: 'stretch',
    margin: '8px 0',
    background: 'linear-gradient(180deg, transparent 0%, rgba(26,24,21,0.40) 20%, rgba(26,24,21,0.40) 80%, transparent 100%)',
  },
  
  // Content area
  contentArea: {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
  },
  
  // ═══ PART 2: TACTICAL DASHBOARD STYLES ══════════════════════════════════════
  
  // Dashboard Grid - simple flex row with 3 equal cards
  dashboardGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
    marginBottom: 24,
  },
  
  // C1: Action Horizon Container
  actionHorizonCard: {
    flex: 1,
    padding: 20,
  },
  
  // C2: Card Title/Subtitle
  cardHeader: {
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // C3: Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    textAlign: 'center',
  },
  emptyIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.successBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  
  // C4: Horizon Row
  horizonRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: 10,
    marginBottom: 8,
    background: COLORS.surface,
    border: `1px solid ${COLORS.goldBorder}`,
    transition: 'all 0.15s',
  },
  
  // C5: Horizon Row Alert Style
  horizonRowAlert: {
    background: COLORS.errorLight,
    borderColor: 'rgba(178,78,78,0.2)',
  },
  
  // C6: Flagged Icon
  flaggedIcon: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: COLORS.error,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    flexShrink: 0,
  },
  
  // C7 & C8: Horizon Label/Meta
  horizonInfo: {
    flex: 1,
    minWidth: 0,
  },
  horizonLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  horizonMeta: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // C9: Pay Now Button
  payNowBtn: {
    padding: '6px 14px',
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    color: COLORS.white,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  
  // C12: Vendor Chip
  vendorChip: {
    padding: '2px 8px',
    borderRadius: 4,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginLeft: 8,
  },
  
  // D1: Gauge Container
  gaugeCard: {
    flex: 1,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  
  // D2-D6: SVG Gauge
  gaugeWrapper: {
    position: 'relative',
    width: 140,
    height: 140,
    marginBottom: 16,
  },
  gaugeCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  gaugeCenterPct: {
    fontSize: 28,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1,
  },
  gaugeCenterLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: 4,
  },
  
  // D7: Gauge Legend
  gaugeLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: COLORS.textMuted,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: COLORS.successLight,
  },
  
  // E1: Liquidity Card Container
  liquidityCard: {
    flex: 1,
    padding: 20,
  },
  liquidityCardAlert: {
    borderColor: COLORS.warningBorder,
  },
  
  // E3 & E4: Cash/Committed Display
  liquidityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  liquidityItem: {
    textAlign: 'center',
  },
  liquidityLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  liquidityValue: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  liquidityValueGreen: {
    color: COLORS.successLight,
  },
  
  // E5 & E6: Liquidity Track
  liquidityTrack: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    background: '#EEE6D7',
    overflow: 'hidden',
    marginBottom: 8,
  },
  liquidityFill: {
    height: '100%',
    borderRadius: 4,
    background: `linear-gradient(90deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    transition: 'width 0.3s ease',
  },
  
  // E7: Liquidity Caption
  liquidityCaption: {
    fontSize: 11,
    color: COLORS.textMuted,
    textAlign: 'center',
    marginBottom: 12,
  },
  
  // E8 & E9: Alert Banner
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 8,
    background: COLORS.warningLight,
    border: `1px solid ${COLORS.warningBorder}`,
  },
  alertBannerText: {
    fontSize: 11,
    color: '#92400e',
    fontWeight: 500,
  },
  
  // ═══ PART 3: QUICK ENTRY STRIP STYLES ═══════════════════════════════════════
  
  // F1: Entry Strip Float (absolute positioned)
  entryStripFloat: {
    position: 'relative',
    zIndex: 10,
    marginBottom: 20,
    animation: 'slideDown 0.25s ease-out',
  },
  
  // F2: Entry Strip Card - Standardized width to match Vendors modal (680px)
  entryStripCard: {
    background: COLORS.white,
    borderRadius: 14,
    border: `2px solid ${COLORS.gold}`,
    padding: 24,
    boxShadow: '0 8px 24px rgba(198,167,90,0.15)',
    maxWidth: 680,
    margin: '0 auto',
  },
  
  // F3: Entry Strip Header
  entryStripHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: `1px solid ${COLORS.goldBorder}`,
  },
  entryStripTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  entryStripClose: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  
  // F4-F6: Type Toggle Row
  typeToggleRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
  },
  typeChip: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textMuted,
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  typeChipActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
    color: COLORS.gold,
  },
  
  // F7: Form Grid
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 20,
  },
  formGridFull: {
    gridColumn: '1 / -1',
  },
  
  // F8-F11: Form Inputs
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  formLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  formInput: {
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    fontSize: 14,
    color: COLORS.textPrimary,
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  formInputFocus: {
    borderColor: COLORS.gold,
  },
  
  // F12: Category Chips
  categoryChipsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  categoryChip: {
    padding: '8px 14px',
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textMuted,
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  categoryChipActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
    color: COLORS.gold,
  },
  
  // F13: Payment Method Chips (same styling as category)
  
  // F14: In-Kind Note
  inKindNote: {
    fontSize: 11,
    fontStyle: 'italic',
    color: COLORS.textMuted,
    marginTop: -8,
    marginBottom: 16,
  },
  
  // F15: Notes Textarea
  formTextarea: {
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    fontSize: 14,
    color: COLORS.textPrimary,
    outline: 'none',
    resize: 'vertical',
    minHeight: 80,
    fontFamily: 'inherit',
  },
  
  // F16 & F17: Action Buttons
  entryButtonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 20,
    paddingTop: 16,
    borderTop: `1px solid ${COLORS.goldBorder}`,
  },
  cancelBtn: {
    padding: '12px 24px',
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textMuted,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  saveEntryBtn: {
    padding: '12px 28px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.white,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  saveEntryBtnDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  
  // G1: Receipt Zone Container
  receiptZone: {
    marginTop: 16,
    padding: 20,
    borderRadius: 12,
    border: `2px dashed ${COLORS.goldBorder}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  receiptZoneHover: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
  },
  receiptZoneAttached: {
    borderStyle: 'solid',
    borderColor: COLORS.successLight,
    background: 'rgba(90,158,82,0.05)',
  },
  
  // G2: Receipt Empty State
  receiptEmptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center',
  },
  receiptIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  receiptText: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  receiptMeta: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // G3: Receipt Attached State
  receiptAttached: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  receiptFileIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.successBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  receiptFileInfo: {
    flex: 1,
  },
  receiptFileName: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 2,
  },
  receiptFileSize: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  receiptRemoveBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  
  // ═══ PART 4: MASTER LEDGER STYLES ═══════════════════════════════════════════
  
  // H1: Ledger Section Container
  ledgerSection: {
    marginTop: 24,
  },
  
  // H2: Ledger Section Header
  ledgerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  ledgerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  ledgerCount: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginLeft: 8,
  },
  
  // H3: Search Bar
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    maxWidth: 280,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: 13,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  searchClear: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // H5: Empty State
  ledgerEmptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
  },
  ledgerEmptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  ledgerEmptyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  ledgerEmptyText: {
    fontSize: 13,
    color: COLORS.textMuted,
  },
  
  // H6: Table Card
  tableCard: {
    background: COLORS.white,
    borderRadius: 14,
    border: `1px solid ${COLORS.goldBorder}`,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(18,18,18,0.04)',
  },
  
  // H7: Table Frame
  tableFrame: {
    overflowX: 'auto',
    minWidth: 900,
  },
  
  // I1 & N1: Section Sub-Headers
  sectionSubHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    background: COLORS.goldLight,
    borderBottom: `1px solid ${COLORS.goldBorder}`,
  },
  sectionSubHeaderText: {
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.gold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  // I2 & N2: Header Row
  tableHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.goldBorder}`,
    background: theme.bgCard,
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    cursor: 'help',
  },
  
  // I3: Column Widths
  colLabel: { width: 200, flexShrink: 0 },
  colCategory: { width: 100, flexShrink: 0 },
  colAmount: { width: 90, flexShrink: 0, textAlign: 'right' },
  colPaid: { width: 90, flexShrink: 0, textAlign: 'right' },
  colDue: { width: 100, flexShrink: 0 },
  colMethod: { width: 80, flexShrink: 0 },
  colStatus: { width: 90, flexShrink: 0 },
  colActions: { width: 100, flexShrink: 0, textAlign: 'center' },
  
  // J1: Ledger Row Container
  ledgerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    borderBottom: `1px solid ${COLORS.goldBorder}`,
    transition: 'background 0.15s',
    cursor: 'pointer',
  },
  ledgerRowHover: {
    background: 'rgba(198,167,90,0.04)',
  },
  
  // J2: Row Amber State
  ledgerRowAmber: {
    background: COLORS.amberLight,
  },
  
  // J3: Row Red State
  ledgerRowRed: {
    background: COLORS.errorLight,
  },
  
  // J4: Cell Primary Style
  cellPrimary: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  
  // J5: Cell Sub Style
  cellSub: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // J6: Amount Formatting
  amountGreen: {
    color: COLORS.successLight,
  },
  
  // J7: Needs Review Badge
  needsReviewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 4,
    background: 'rgba(245,158,11,0.1)',
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.amber,
  },
  needsReviewDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: COLORS.amber,
  },
  
  // J8: Status Badge
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
  },
  statusActive: {
    background: 'rgba(90,158,82,0.1)',
    color: COLORS.successLight,
  },
  statusUrgent: {
    background: 'rgba(178,78,78,0.1)',
    color: COLORS.error,
  },
  statusPaused: {
    background: 'rgba(217,119,6,0.1)',
    color: COLORS.amber,
  },
  statusArchived: {
    background: 'rgba(125,125,125,0.1)',
    color: '#7D7D7D',
  },
  statusCancelled: {
    background: 'rgba(255,107,107,0.15)',
    color: '#ff6b6b',
  },
  
  // J9: Note Button
  noteBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  
  // K1: Control Panel Container
  controlPanel: {
    padding: 16,
    background: COLORS.amberLight,
    borderBottom: `1px solid ${COLORS.warningBorder}`,
  },
  
  // K2: Reason Input
  reasonInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.white,
    fontSize: 13,
    color: COLORS.textPrimary,
    outline: 'none',
    marginBottom: 12,
  },
  
  // K3: Control Button Row
  controlBtnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  
  // K4-K6: Control Buttons
  controlBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  controlBtnFlag: {
    background: COLORS.errorLight,
    color: COLORS.error,
    border: `1px solid rgba(178,78,78,0.2)`,
  },
  controlBtnPause: {
    background: COLORS.amberLight,
    color: COLORS.amber,
    border: `1px solid rgba(217,119,6,0.2)`,
  },
  controlBtnCancel: {
    background: '#1A1A1A',
    color: COLORS.white,
  },
  
  // L1: Journal Section
  journalSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: `1px solid ${COLORS.goldBorder}`,
  },
  
  // L2: Journal Title
  journalTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 8,
  },
  
  // L3: Journal Note Card
  journalNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    background: COLORS.white,
    border: `1px solid ${COLORS.goldBorder}`,
    marginBottom: 8,
  },
  journalNoteText: {
    flex: 1,
    fontSize: 12,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
  },
  journalNoteDate: {
    fontSize: 10,
    color: COLORS.textMuted,
    marginTop: 4,
  },
  journalRemoveBtn: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // L4 & L5: Journal Input Row
  journalInputRow: {
    display: 'flex',
    gap: 8,
  },
  journalInput: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.white,
    fontSize: 12,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  journalAddBtn: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // M1: CR Flow Container
  crFlowContainer: {
    padding: 20,
    background: COLORS.white,
    borderRadius: 12,
    border: `1px solid ${COLORS.goldBorder}`,
    marginTop: 12,
  },
  
  // M2: CR Title
  crTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 12,
  },
  
  // M3: Impact Grid
  impactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginBottom: 16,
  },
  impactCell: {
    textAlign: 'center',
    padding: 12,
    borderRadius: 8,
    background: COLORS.surface,
    border: `1px solid ${COLORS.goldBorder}`,
  },
  impactLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    marginBottom: 4,
  },
  impactValue: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  impactValueRed: {
    color: COLORS.error,
  },
  impactValueGreen: {
    color: COLORS.successLight,
  },
  
  // M5: Replacement Options Title
  replacementTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 12,
  },
  
  // M6: Replacement Card Scroll
  replacementScroll: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 8,
  },
  
  // M7: Replacement Card
  replacementCard: {
    minWidth: 180,
    padding: 14,
    borderRadius: 10,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.surface,
    flexShrink: 0,
  },
  replacementName: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  replacementMeta: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginBottom: 8,
  },
  
  // M8: Rating Row
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 10,
  },
  ratingText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  
  // M9: Inquire Button
  inquireBtn: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.white,
    cursor: 'pointer',
  },
  
  // M10: Close Button (CR Flow)
  crCloseBtn: {
    marginTop: 12,
    padding: '10px 20px',
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textMuted,
    cursor: 'pointer',
    width: '100%',
  },
  
  // O4: Expand Button
  expandBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  
  // O5: Sub-Rows Container
  subRowsContainer: {
    marginLeft: 24,
    paddingLeft: 16,
    borderLeft: `2px solid ${COLORS.goldBorder}`,
    marginTop: 8,
    marginBottom: 8,
  },
  
  // O6: No Payments Text
  noPaymentsText: {
    fontSize: 12,
    fontStyle: 'italic',
    color: COLORS.textMuted,
    padding: '8px 0',
  },
  
  // O7: Payment Sub-Row
  paymentSubRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    borderRadius: 6,
    background: COLORS.surface,
    marginBottom: 6,
  },
  paymentAmount: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.successLight,
  },
  paymentMeta: {
    flex: 1,
    fontSize: 11,
    color: COLORS.textMuted,
  },
  paymentRemoveBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // O8: Add Payment Button
  addPaymentBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.gold,
    cursor: 'pointer',
  },
  
  // O9: Add Payment Form
  addPaymentForm: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    background: COLORS.surface,
    marginTop: 8,
  },
  
  // Action Buttons (Edit/Delete)
  actionBtnRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  actionBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  actionBtnDelete: {
    '&:hover': {
      background: COLORS.errorLight,
      borderColor: COLORS.error,
    }
  },
  addPaymentInput: {
    padding: '8px 10px',
    borderRadius: 6,
    border: `1px solid ${COLORS.goldBorder}`,
    background: COLORS.white,
    fontSize: 12,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  addPaymentBtnRow: {
    gridColumn: '1 / -1',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 4,
  },
  addPaymentCancel: {
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.textMuted,
    cursor: 'pointer',
  },
  addPaymentSubmit: {
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: COLORS.gold,
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.white,
    cursor: 'pointer',
  },
  
  // ═══ PART 5: EXPORT ZONE & ANIMATION STYLES ═════════════════════════════════
  
  // P1: Export Zone Container
  exportZone: {
    marginTop: 24,
    padding: 24,
    background: COLORS.white,
    borderRadius: 14,
    border: `1px solid ${COLORS.goldBorder}`,
    boxShadow: '0 4px 12px rgba(18,18,18,0.04)',
  },
  
  exportHeader: {
    marginBottom: 16,
  },
  exportTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  exportSubtitle: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  
  // P2: Export Button Row
  exportButtonRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  
  // P3: Preview Text Export Button
  exportBtnOutline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 10,
    border: `1px solid ${COLORS.gold}`,
    background: 'transparent',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.gold,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  
  // P4: Open PDF Report Button
  exportBtnSolid: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.white,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  
  // P5: Export Note
  exportNote: {
    fontSize: 11,
    color: COLORS.textMuted,
    fontStyle: 'italic',
    marginBottom: 16,
  },
  
  // P6: iCal Info Row
  icalRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    borderRadius: 10,
    background: COLORS.surface,
    border: `1px solid ${COLORS.goldBorder}`,
  },
  icalIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icalInfo: {
    flex: 1,
    minWidth: 0,
  },
  icalLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 2,
  },
  
  // P7: iCal URL Link
  icalUrl: {
    fontSize: 12,
    color: COLORS.gold,
    textDecoration: 'underline',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  
  // P8: iCal Copy Button
  icalCopyBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: `1px solid ${COLORS.goldBorder}`,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  
  // Q3: Scroll Overlay
  scrollOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.38)',
    zIndex: 5,
    transition: 'opacity 0.25s ease',
  },
  
  // Text Preview Modal
  textPreviewModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  textPreviewCard: {
    width: '100%',
    maxWidth: 600,
    maxHeight: '80vh',
    borderRadius: 14,
    background: COLORS.white,
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  textPreviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: `1px solid ${COLORS.goldBorder}`,
  },
  textPreviewTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  textPreviewBody: {
    flex: 1,
    padding: 20,
    overflowY: 'auto',
    background: theme.bgCard,
  },
  textPreviewContent: {
    fontFamily: 'Monaco, Menlo, monospace',
    fontSize: 12,
    lineHeight: 1.6,
    color: COLORS.textPrimary,
    whiteSpace: 'pre-wrap',
  },
  textPreviewFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 20px',
    borderTop: `1px solid ${COLORS.goldBorder}`,
  },
  
  // Part 5 placeholder
  placeholderCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    borderRadius: 14,
    border: `1px dashed ${COLORS.goldBorder}`,
    background: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
  },
  placeholderTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 13,
    color: COLORS.textMuted,
    maxWidth: 320,
    lineHeight: 1.5,
  },
  placeholderBadge: {
    marginTop: 16,
    padding: '8px 16px',
    borderRadius: 8,
    background: 'rgba(198,167,90,0.1)',
    border: `1px solid ${COLORS.goldBorder}`,
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.gold,
  },
};

// ─── B1-B11: Global KPI Strip Component ─────────────────────────────────────────

function GlobalKPIStrip({ budget, vendors, financeEntries = [], sponsors = [], onBudgetUpdate }) {
  // One generalized editor for the money field being edited ('self' funds).
  const [editingField, setEditingField] = useState(null); // 'self' | 'budget' | null
  const [fieldInput, setFieldInput] = useState('');
  const [savingField, setSavingField] = useState(false);
  // Money view: actual (received) vs theoretical (pledged) sponsor funds.
  const [fundView, setFundView] = useState('received'); // 'received' | 'pledged'

  // ── Funding sources ──
  const selfFunds = Number(budget?.self_funds) || 0;
  const sponsorReceived = (sponsors || []).reduce((s, x) => s + (Number(x.amount_received ?? x.received) || 0), 0);
  const sponsorPledged = (sponsors || []).reduce((s, x) => s + (Number(x.pledge_amount ?? x.pledged) || 0), 0);
  const sponsorFunds = fundView === 'pledged' ? sponsorPledged : sponsorReceived;
  // Effective budget = host's own money + sponsor money. Sponsorship is never the default source.
  const totalBudget = selfFunds + sponsorFunds;

  // ── Spend: money actually paid OUT = misc expense payments + vendor payments.
  // Previously Total Spent counted misc expenses ONLY, so paying a vendor never
  // reduced Total Spent / Available Balance — which made the numbers look wrong.
  // Now it reflects all money out. (Vendor `paid_to_date` comes from backend
  // vendor_to_dict.) Remaining obligations — unpaid vendor balances + unpaid
  // expenses — stay surfaced separately as "Amount Next Due".
  const actualExpenses = financeEntries.filter(e => e.type !== 'sponsor' && !e.cancelled);
  const miscSpent = actualExpenses.reduce((sum, e) => sum + (e.amount_paid ?? 0), 0);
  const vendorSpent = vendors.reduce((sum, v) => sum + (Number(v.paid_to_date) || 0), 0);
  const totalSpent = miscSpent + vendorSpent;

  // Available Balance = Total Budget − all money paid out (misc + vendor)
  const availableBalance = totalBudget - totalSpent;

  // Amount Next Due — informational (unpaid misc + vendor balances)
  const vendorNextDue = vendors.reduce((sum, v) => sum + (Number(v.balance_amount) || 0), 0);
  const expenseNextDue = actualExpenses
    .filter(e => e.status !== 'paid' && e.due_date)
    .reduce((sum, e) => sum + ((e.contract_amount || 0) - (e.amount_paid || 0)), 0);
  const amountNextDue = budget?.next_due_amount ?? (vendorNextDue + expenseNextDue);

  const handleFieldSave = async () => {
    const val = parseFloat(fieldInput);
    if (isNaN(val) || val < 0) return;
    setSavingField(true);
    try {
      // Persist the edited field. Keep total_budget in sync as the derived
      // (self + received-sponsor) pool so other tabs (e.g. Dashboard) stay consistent.
      const body = {};
      if (editingField === 'self') {
        body.self_funds = val;
        body.budget_total = val + sponsorReceived;
      } else {
        body.budget_total = val;
      }
      const res = await fetch(`${API}/api/budget`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        onBudgetUpdate?.(data);
        setEditingField(null);
      }
    } catch {}
    setSavingField(false);
  };

  // KPI definitions — host funding model
  const kpis = [
    { label: 'Self Funds', value: fmt(selfFunds), editable: true, editKey: 'self' },
    { label: `Sponsor Funds (${fundView === 'pledged' ? 'Pledged' : 'Received'})`, value: fmt(sponsorFunds) },
    { label: 'Total Budget', value: fmt(totalBudget) },
    { label: 'Total Spent', value: fmt(totalSpent) },
    { label: 'Available Balance', value: fmt(availableBalance), warn: availableBalance < 0 },
    { label: 'Amount Next Due', value: fmt(amountNextDue) },
  ];

  return (
    // B1: KPI Strip Container
    <div style={styles.kpiStrip} data-testid="finance-kpi-strip">
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600 }}>Sponsor funds shown as:</span>
        <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }} data-testid="fund-view-toggle">
          <button onClick={() => setFundView('received')}
            title="Actual balance — counts only sponsor money already received"
            style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', border: 'none', cursor: 'pointer',
              background: fundView === 'received' ? COLORS.gold : 'transparent', color: fundView === 'received' ? '#1A1A1A' : COLORS.textMuted }}>
            Received (actual)
          </button>
          <button onClick={() => setFundView('pledged')}
            title="Theoretical balance — also counts pledged sponsor money that may not have arrived yet"
            style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', border: 'none', cursor: 'pointer',
              background: fundView === 'pledged' ? COLORS.gold : 'transparent', color: fundView === 'pledged' ? '#1A1A1A' : COLORS.textMuted }}>
            Pledged (theoretical)
          </button>
        </div>
      </div>
      <div style={styles.kpiRow}>
        {kpis.map((kpi, index) => (
          <React.Fragment key={kpi.label}>
            {/* B10: KPI Dividers */}
            {index > 0 && <div style={styles.kpiDivider} />}
            
            {/* B2-B7: KPI Cells */}
            <div style={styles.kpiCell} data-testid={`kpi-${kpi.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              {/* B8: KPI Cell Labels */}
              <span style={styles.kpiCellLabel}>
                {kpi.label}
                {kpi.editable && editingField !== kpi.editKey && (
                  <span onClick={() => { setFieldInput(String((kpi.editKey === 'self' ? selfFunds : totalBudget) || '')); setEditingField(kpi.editKey); }}
                    style={{ marginLeft: 6, cursor: 'pointer', fontSize: 10, color: COLORS.gold, opacity: 0.7 }}
                    data-testid={`edit-${kpi.editKey}-btn`}>
                    ✎
                  </span>
                )}
              </span>

              {/* B9: KPI Cell Values with conditional colors */}
              {kpi.editable && editingField === kpi.editKey ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 13, color: '#A0A0A0' }}>$</span>
                  <input
                    data-testid={`${kpi.editKey}-input`}
                    type="number"
                    value={fieldInput}
                    onChange={e => setFieldInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleFieldSave(); if (e.key === 'Escape') setEditingField(null); }}
                    autoFocus
                    style={{ width: 90, padding: '2px 6px', borderRadius: 4, border: `1px solid ${COLORS.gold}`,
                      background: '#2A2A2A', fontSize: 16, color: '#FFFFFF', fontFamily: "Georgia, serif",
                      fontWeight: 700, outline: 'none' }}
                  />
                  <button onClick={handleFieldSave} disabled={savingField}
                    data-testid={`${kpi.editKey}-save-btn`}
                    style={{ padding: '2px 8px', borderRadius: 4, border: `1px solid ${COLORS.gold}`,
                      background: 'transparent', color: COLORS.gold, fontSize: 11, fontWeight: 700,
                      cursor: 'pointer' }}>
                    {savingField ? '...' : '✓'}
                  </button>
                </div>
              ) : (
                <span style={{
                  ...styles.kpiCellValue,
                  ...(kpi.warn ? { color: COLORS.error } : {}),
                  ...(kpi.green ? { color: COLORS.successLight } : {}),
                }}>
                  {kpi.value}
                </span>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 2: TACTICAL DASHBOARD COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── C1-C13: Action Horizon Card ────────────────────────────────────────────────

function ActionHorizonCard({ vendors, financeEntries, onMarkPaid }) {
  const [payingId, setPayingId] = useState(null);

  // Build horizon tasks from vendors with upcoming due dates
  const horizonTasks = [];
  
  // C11: Vendor Due Rows.
  // Vendors expose REAL fields from backend vendor_to_dict: contract_total,
  // paid_to_date, balance_amount (= contract_total - paid_to_date), deposit_amount,
  // deposit_due_date, balance_due_date, company_name, needs_attention.
  // (There is NO next_payment_due / next_payment_amount / amount_paid / contract_amount
  // on vendors.) Surface the soonest UNMET payment obligation, mirroring the backend
  // payment-status order: an unpaid deposit (with deposit_due_date) first, otherwise
  // the remaining balance (with balance_due_date). Only show rows that have both a real
  // due date and a real amount still owed — no fabricated placeholders.
  vendors.forEach(vendor => {
    const contractTotal = vendor.contract_total || 0;
    const paidToDate = vendor.paid_to_date || 0;
    const depositAmount = vendor.deposit_amount || 0;
    const balanceRemaining = vendor.balance_amount != null
      ? vendor.balance_amount
      : Math.max(0, contractTotal - paidToDate);

    let dueDate = null;
    let amount = 0;

    const depositUnpaid = depositAmount > 0 && paidToDate < depositAmount;
    if (depositUnpaid && vendor.deposit_due_date) {
      // Amount still owed toward the deposit (never more than the remaining balance).
      dueDate = vendor.deposit_due_date;
      amount = Math.min(depositAmount - paidToDate, balanceRemaining);
    } else if (balanceRemaining > 0 && vendor.balance_due_date) {
      dueDate = vendor.balance_due_date;
      amount = balanceRemaining;
    }

    if (dueDate && amount > 0) {
      horizonTasks.push({
        id: vendor._id || vendor.id,
        type: 'vendor',
        label: vendor.company_name || 'Vendor',
        amount,
        dueDate,
        flagged: !!vendor.needs_attention,
      });
    }
  });

  // Add ONLY expense entries with due dates (exclude fully paid AND sponsors)
  // Sponsors are NOT payables - they are donations/income
  financeEntries
    .filter(entry => entry.type !== 'sponsor' && !entry.cancelled)
    .forEach(entry => {
      // Skip if already paid or fully paid
      const isPaid = entry.status === 'paid' || (entry.amount_paid >= entry.contract_amount);
      if (entry.due_date && !isPaid) {
        const remainingAmount = (entry.contract_amount || 0) - (entry.amount_paid || 0);
        horizonTasks.push({
          id: entry._id || entry.id,
          type: 'expense',
          label: entry.label || entry.name,
          amount: remainingAmount,
          dueDate: entry.due_date,
          flagged: entry.flagged,
        });
      }
    });

  // Sort by due date
  horizonTasks.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  // C9 & C10: Pay Now handler
  const handlePayNow = async (task) => {
    // C13: Mark Paid Confirmation
    if (!window.confirm(`Mark "${task.label}" as paid (${fmt(task.amount)})?`)) {
      return;
    }
    
    setPayingId(task.id);
    try {
      if (onMarkPaid) {
        await onMarkPaid(task);
      }
    } catch (err) {
      console.error('Failed to mark as paid:', err);
      toast.error(`Failed to mark "${task.label}" as paid`);
    } finally {
      setPayingId(null);
    }
  };

  // Format date for display
  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    return `Due ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    // C1: Action Horizon Container
    <div className="dashboard-card" style={styles.actionHorizonCard} data-testid="action-horizon-card">
      {/* C2: Card Title/Subtitle */}
      <div style={styles.cardHeader}>
        <div style={styles.cardTitle}>Action Horizon</div>
        <div style={styles.cardSubtitle}>Next financial tasks</div>
      </div>

      {horizonTasks.length === 0 ? (
        // C3: Empty State
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <Check size={20} color={COLORS.successLight} />
          </div>
          <div style={styles.emptyText}>No pending tasks</div>
        </div>
      ) : (
        <div>
          {horizonTasks.slice(0, 5).map(task => (
            // C4 & C5: Horizon Row with alert styling
            <div
              key={task.id}
              style={{
                ...styles.horizonRow,
                ...(task.flagged ? styles.horizonRowAlert : {}),
              }}
              data-testid={`horizon-row-${task.id}`}
            >
              {/* C6: Flagged Icon */}
              {task.flagged && (
                <div style={styles.flaggedIcon}>
                  <AlertCircle size={12} color={COLORS.white} />
                </div>
              )}
              
              {/* C7 & C8: Horizon Label/Meta */}
              <div style={styles.horizonInfo}>
                <div style={styles.horizonLabel}>
                  {task.label}
                  {/* C12: Vendor Chip */}
                  {task.type === 'vendor' && (
                    <span style={styles.vendorChip}>Vendor</span>
                  )}
                </div>
                <div style={styles.horizonMeta}>
                  {fmt(task.amount)} · {formatDueDate(task.dueDate)}
                </div>
              </div>
              
              {/* C9 & C10: Pay Now Button with loading state */}
              <button
                style={styles.payNowBtn}
                onClick={() => handlePayNow(task)}
                disabled={payingId === task.id}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                data-testid={`pay-btn-${task.id}`}
              >
                {payingId === task.id ? (
                  <span style={{ fontSize: 10 }}>...</span>
                ) : (
                  'Pay'
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── D1-D8: Burn Rate Gauge (SVG Donut) ─────────────────────────────────────────

function BurnRateGauge({ budget, vendors, financeEntries = [] }) {
  const totalAllowed = budget?.total_budget ?? 0;

  // Total Spent = vendors.paid_to_date + expenses.amount_paid (matches GlobalKPIStrip).
  // NOTE: vendors expose `paid_to_date` (backend vendor_to_dict), NOT `amount_paid`.
  // Exclude sponsors (income, not spend) and cancelled entries so the gauge agrees
  // with the KPI strip's Total Spent.
  const vendorSpent = vendors.reduce((sum, v) => sum + (v.paid_to_date ?? 0), 0);
  const expenseSpent = financeEntries
    .filter(e => e.type !== 'sponsor' && !e.cancelled)
    .reduce((sum, e) => sum + (e.amount_paid ?? 0), 0);
  const totalSpent = vendorSpent + expenseSpent;
  
  const reservePercent = budget?.reserve_percent ?? CONTINGENCY_PCT * 100;
  const contingency = budget?.reserve_amount ?? (totalAllowed * (reservePercent / 100));
  
  // Calculate percentages
  const spentPct = totalAllowed > 0 ? (totalSpent / totalAllowed) * 100 : 0;
  const contingencyPct = reservePercent;  // Use actual reserve percent from budget
  
  // D8: Color Logic - Gold < 85%, warning 85-100%, error > 100%
  const getSpentColor = () => {
    if (spentPct > 100) return COLORS.error;
    if (spentPct >= 85) return COLORS.amber;
    return COLORS.gold;
  };

  // SVG parameters
  const size = 140;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // D3: Contingency arc (15%)
  const contingencyOffset = circumference * (1 - contingencyPct / 100);
  
  // D4: Spent arc
  const spentOffset = circumference * (1 - Math.min(spentPct, 100) / 100);

  return (
    // D1: Gauge Container
    <div className="dashboard-card" style={styles.gaugeCard} data-testid="burn-rate-gauge">
      {/* C2: Card Title */}
      <div style={{ ...styles.cardHeader, textAlign: 'center', marginBottom: 12 }}>
        <div style={styles.cardTitle}>Burn Rate</div>
      </div>
      
      {/* D2-D6: SVG Donut */}
      <div style={styles.gaugeWrapper}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* D2: SVG Donut Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#EEE6D7"
            strokeWidth={strokeWidth}
          />
          
          {/* D3: Contingency Arc (muted green) */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(90,158,82,0.3)"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={contingencyOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
          
          {/* D4: Spent Arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={getSpentColor()}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={spentOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        
        {/* D5 & D6: Center Percentage + Label */}
        <div style={styles.gaugeCenter}>
          <div style={{ ...styles.gaugeCenterPct, color: getSpentColor() }}>
            {Math.round(spentPct)}%
          </div>
          <div style={styles.gaugeCenterLabel}>Burn Rate</div>
        </div>
      </div>
      
      {/* D7: Gauge Legend */}
      <div style={styles.gaugeLegend}>
        <div style={styles.legendDot} />
        <span>{contingencyPct}% Reserve Locked · {fmt(contingency)}</span>
      </div>
    </div>
  );
}

// ─── E1-E9: Liquidity Flow Bar ──────────────────────────────────────────────────

function LiquidityFlowBar({ liquidity, sponsors, vendors, financeEntries = [] }) {
  // Prefer canonical backend values from GET /api/liquidity (source of truth).
  // Client-side fallback is retained so the card still renders if the endpoint
  // transiently fails; it mirrors the backend formula in _compute_liquidity().
  const cashReceived = liquidity?.cash_received ??
    sponsors.reduce((sum, s) => sum + (s.amount_received || s.received || 0), 0);

  const vendorCommitted = vendors.reduce((sum, v) => sum + (v.contract_total || v.contract_amount || 0), 0);
  const expenseCommitted = financeEntries
    .filter(e => !e.cancelled)
    .reduce((sum, e) => sum + (e.contract_amount || 0), 0);
  const committed = liquidity?.committed ?? (vendorCommitted + expenseCommitted);

  // Coverage percentage — prefer backend-derived value.
  const fallbackCoverage = committed > 0
    ? Math.max(0, Math.min((cashReceived / committed) * 100, 100))
    : 100;
  const coveragePct = liquidity?.coverage_percent ?? fallbackCoverage;

  // Liquidity gap — prefer backend-derived value.
  const liquidityGap = liquidity?.liquidity_gap ?? Math.max(committed - cashReceived, 0);
  const hasGap = liquidity?.has_gap ?? (liquidityGap > 0);

  return (
    // E1: Liquidity Card Container (with alert styling if gap exists)
    <div 
      className="dashboard-card"
      style={{
        ...styles.liquidityCard,
        ...(hasGap ? styles.liquidityCardAlert : {}),
      }}
      data-testid="liquidity-flow-bar"
    >
      {/* E2: Card Title */}
      <div style={styles.cardHeader}>
        <div style={styles.cardTitle}>Cash Liquidity</div>
      </div>
      
      {/* E3 & E4: Cash Received / Committed Display */}
      <div style={styles.liquidityRow}>
        <div style={styles.liquidityItem}>
          <div style={styles.liquidityLabel}>Cash Received</div>
          <div style={{ ...styles.liquidityValue, ...styles.liquidityValueGreen }}>
            {fmt(cashReceived)}
          </div>
          <div style={{ fontSize: 10, color: '#6B6358', marginTop: 2 }}>
            from sponsors
          </div>
        </div>
        <div style={styles.liquidityItem}>
          <div style={styles.liquidityLabel}>Committed</div>
          <div style={styles.liquidityValue}>{fmt(committed)}</div>
          <div style={{ fontSize: 10, color: '#6B6358', marginTop: 2 }}>
            {vendorCommitted > 0 && expenseCommitted > 0 
              ? `vendors (${fmt(vendorCommitted)}) + expenses (${fmt(expenseCommitted)})`
              : vendorCommitted > 0 
                ? `from vendor contracts`
                : expenseCommitted > 0
                  ? `from finance entries`
                  : 'total obligations'}
          </div>
        </div>
      </div>
      
      {/* E5 & E6: Liquidity Track + Fill */}
      <div style={styles.liquidityTrack}>
        <div 
          style={{
            ...styles.liquidityFill,
            width: `${coveragePct}%`,
          }}
        />
      </div>
      
      {/* E7: Liquidity Caption */}
      <div style={styles.liquidityCaption}>
        {Math.round(coveragePct)}% of committed obligations covered
      </div>
      
      {/* E8 & E9: Alert Banner (when gap exists) */}
      {hasGap && (
        <div style={styles.alertBanner} data-testid="liquidity-gap-alert">
          <AlertTriangle size={16} color="#f59e0b" />
          <span style={styles.alertBannerText}>
            Liquidity Gap: {fmt(liquidityGap)}. Follow up with sponsors.
          </span>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 3: QUICK ENTRY STRIP COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// F12: Category Options
const CATEGORY_OPTIONS = [
  'Culinary',
  'Production',
  'Entertainment',
  'Venue',
  'Floral',
  'Photography',
  'Other',
];

// F13: Payment Method Options
const PAYMENT_METHOD_OPTIONS = [
  'Cash',
  'Check',
  'Credit',
  'Zelle',
  'Wire',
  'In-Kind',
];

// ─── F1-F18, G1-G4: Quick Entry Strip ───────────────────────────────────────────

// Currency formatting helper
const formatCurrencyInput = (value) => {
  // Remove non-numeric except decimal
  const numericValue = value.replace(/[^\d.]/g, '');
  // Parse and format with commas
  const parts = numericValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts[1]?.length > 2) parts[1] = parts[1].slice(0, 2);
  return parts.join('.');
};

const parseCurrencyInput = (formatted) => {
  // Remove commas and convert to number
  return parseFloat(formatted.replace(/,/g, '')) || 0;
};

function QuickEntryStrip({ onClose, onSave }) {
  // Form state
  const [entryType, setEntryType] = useState('expense'); // 'expense' | 'sponsor'
  const [label, setLabel] = useState('');
  const [contractAmount, setContractAmount] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [receivedDate, setReceivedDate] = useState(''); // For tracking when payment was received
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [showOnGuestPage, setShowOnGuestPage] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // G1-G4: Receipt state
  const [receiptFile, setReceiptFile] = useState(null);
  const [isHoveringReceipt, setIsHoveringReceipt] = useState(false);
  const fileInputRef = useRef(null);

  // Currency input handlers
  const handleContractAmountChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value);
    setContractAmount(formatted);
  };
  
  const handleAmountPaidChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value);
    setAmountPaid(formatted);
  };

  // Validation
  const isFormValid = label.trim() && contractAmount && parseCurrencyInput(contractAmount) > 0;

  // G4: File Picker Integration
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (image/PDF)
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Please select an image or PDF file');
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleRemoveReceipt = (e) => {
    e.stopPropagation();
    setReceiptFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // F17 & F18: Save handler
  const handleSave = async () => {
    if (!isFormValid) return;
    
    setIsSaving(true);
    try {
      const entryData = {
        type: entryType,
        label: label.trim(),
        contract_amount: parseCurrencyInput(contractAmount),
        amount_paid: parseCurrencyInput(amountPaid) || 0,
        due_date: dueDate || null,
        received_date: receivedDate || null, // Track when payment was received
        category: category || null,
        payment_method: paymentMethod || null,
        notes: notes.trim() || null,
        has_receipt: !!receiptFile,
        show_on_guest_page: showOnGuestPage,
        display_name: displayName.trim() || null,
      };
      
      if (onSave) {
        await onSave(entryData, receiptFile);
      }
      
      onClose();
    } catch (err) {
      console.error('Failed to save entry:', err);
      toast.error('Failed to process entry');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // F1: Entry Strip Float
    <div style={styles.entryStripFloat} data-testid="quick-entry-strip">
      {/* F2: Entry Strip Card */}
      <div style={styles.entryStripCard}>
        {/* F3: Entry Strip Header */}
        <div style={styles.entryStripHeader}>
          <span style={styles.entryStripTitle}>New Finance Entry</span>
          <button
            style={styles.entryStripClose}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            data-testid="entry-close-btn"
          >
            <X size={16} color={COLORS.textMuted} />
          </button>
        </div>

        {/* F4-F6: Type Toggle Row */}
        <div style={styles.typeToggleRow}>
          <button
            style={{
              ...styles.typeChip,
              ...(entryType === 'expense' ? styles.typeChipActive : {}),
            }}
            onClick={() => setEntryType('expense')}
            data-testid="type-expense"
          >
            Expense
          </button>
          <button
            style={{
              ...styles.typeChip,
              ...(entryType === 'sponsor' ? styles.typeChipActive : {}),
            }}
            onClick={() => setEntryType('sponsor')}
            data-testid="type-sponsor"
          >
            Sponsor
          </button>
        </div>

        {/* F7: Form Grid */}
        <div style={styles.formGrid}>
          {/* F8: Label/Vendor Name Input */}
          <div style={{ ...styles.formGroup, ...styles.formGridFull }}>
            <label style={styles.formLabel}>
              {entryType === 'expense' ? 'Vendor / Label' : 'Sponsor Name'}
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={entryType === 'expense' ? 'e.g., Catering by Chef Juan' : 'e.g., Martinez Family'}
              style={styles.formInput}
              onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
              onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
              data-testid="entry-label-input"
            />
          </div>

          {/* F9: Contract Amount Input */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {entryType === 'expense' ? 'Contract Amount' : 'Pledge Amount'}
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.textMuted }}>$</span>
              <input
                type="text"
                inputMode="decimal"
                value={contractAmount}
                onChange={handleContractAmountChange}
                placeholder="0.00"
                style={{ ...styles.formInput, paddingLeft: 24 }}
                onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
                onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
                data-testid="entry-contract-input"
              />
            </div>
          </div>

          {/* F10: Amount Paid Input */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {entryType === 'expense' ? 'Amount Paid' : 'Amount Received'}
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.textMuted }}>$</span>
              <input
                type="text"
                inputMode="decimal"
                value={amountPaid}
                onChange={handleAmountPaidChange}
                placeholder="0.00"
                style={{ ...styles.formInput, paddingLeft: 24 }}
                onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
                onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
                data-testid="entry-paid-input"
              />
            </div>
          </div>

          {/* F11: Due Date Input */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {entryType === 'expense' ? 'Due Date' : 'Expected Date'}
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={styles.formInput}
              onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
              onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
              data-testid="entry-due-date-input"
            />
          </div>
          
          {/* F11b: Received Date Input (for tracking actual payment date) */}
          {(parseCurrencyInput(amountPaid) > 0) && (
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {entryType === 'expense' ? 'Payment Date' : 'Received Date'}
              </label>
              <input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                style={styles.formInput}
                onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
                onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
                data-testid="entry-received-date-input"
              />
            </div>
          )}
        </div>

        {/* F12: Category Chips (Expense only) */}
        {entryType === 'expense' && (
          <>
            <label style={{ ...styles.formLabel, marginBottom: 8, display: 'block' }}>Category</label>
            <div style={styles.categoryChipsRow}>
              {CATEGORY_OPTIONS.map(cat => (
                <button
                  key={cat}
                  style={{
                    ...styles.categoryChip,
                    ...(category === cat ? styles.categoryChipActive : {}),
                  }}
                  onClick={() => setCategory(category === cat ? '' : cat)}
                  type="button"
                  data-testid={`category-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </>
        )}

        {/* F13: Payment Method Chips */}
        <label style={{ ...styles.formLabel, marginBottom: 8, display: 'block' }}>Payment Method</label>
        <div style={styles.categoryChipsRow}>
          {PAYMENT_METHOD_OPTIONS.map(method => (
            <button
              key={method}
              style={{
                ...styles.categoryChip,
                ...(paymentMethod === method ? styles.categoryChipActive : {}),
              }}
              onClick={() => setPaymentMethod(paymentMethod === method ? '' : method)}
              type="button"
              data-testid={`method-${method.toLowerCase()}`}
            >
              {method}
            </button>
          ))}
        </div>

        {/* F14: In-Kind Note */}
        {paymentMethod === 'In-Kind' && (
          <div style={styles.inKindNote}>
            Note: In-Kind contributions are tracked for records but excluded from budget calculations.
          </div>
        )}

        {/* F15: Notes Textarea */}
        <div style={{ ...styles.formGroup, marginTop: 16 }}>
          <label style={styles.formLabel}>Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            style={styles.formTextarea}
            onFocus={(e) => e.currentTarget.style.borderColor = COLORS.gold}
            onBlur={(e) => e.currentTarget.style.borderColor = COLORS.goldBorder}
            data-testid="entry-notes-input"
          />
        </div>

        {/* Guest Page Sponsor Display */}
        <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 12,
          background: 'rgba(198,167,90,0.07)', border: '1px solid rgba(198,167,90,0.22)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>
                Show on Guest Page
              </p>
              <p style={{ fontSize: 10, color: COLORS.textSecondary, margin: '2px 0 0' }}>
                Name appears in the "With Gratitude" section on the guest-facing site
              </p>
            </div>
            <button
              onClick={() => setShowOnGuestPage(v => !v)}
              data-testid="show-on-guest-toggle"
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: showOnGuestPage ? COLORS.gold : 'rgba(198,167,90,0.25)',
                position: 'relative', transition: 'background 0.2s ease', flexShrink: 0,
              }}>
              <div style={{
                position: 'absolute', top: 3, left: showOnGuestPage ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }} />
            </button>
          </div>
          {showOnGuestPage && (
            <div>
              <label style={{ ...styles.formLabel, marginBottom: 4 }}>
                Display As <span style={{ fontWeight: 400, color: COLORS.textSecondary }}>(optional)</span>
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. The Martinez Family, Legacy Events Inc."
                style={{ ...styles.formInput, fontSize: 12 }}
                data-testid="sponsor-display-name"
              />
              <p style={{ fontSize: 9, color: COLORS.textSecondary, margin: '4px 0 0' }}>
                Leave blank to use the entry label above
              </p>
            </div>
          )}
        </div>

        {/* G1-G4: Receipt Upload Zone */}
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileSelect}
          ref={fileInputRef}
          style={{ display: 'none' }}
          data-testid="receipt-file-input"
        />
        <div
          style={{
            ...styles.receiptZone,
            ...(isHoveringReceipt && !receiptFile ? styles.receiptZoneHover : {}),
            ...(receiptFile ? styles.receiptZoneAttached : {}),
          }}
          onClick={() => !receiptFile && fileInputRef.current?.click()}
          onMouseEnter={() => setIsHoveringReceipt(true)}
          onMouseLeave={() => setIsHoveringReceipt(false)}
          data-testid="receipt-zone"
        >
          {receiptFile ? (
            // G3: Receipt Attached State
            <div style={styles.receiptAttached}>
              <div style={styles.receiptFileIcon}>
                <FileText size={20} color={COLORS.successLight} />
              </div>
              <div style={styles.receiptFileInfo}>
                <div style={styles.receiptFileName}>{receiptFile.name}</div>
                <div style={styles.receiptFileSize}>{formatFileSize(receiptFile.size)}</div>
              </div>
              <button
                style={styles.receiptRemoveBtn}
                onClick={handleRemoveReceipt}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = COLORS.errorLight;
                  e.currentTarget.style.borderColor = COLORS.error;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = COLORS.goldBorder;
                }}
                data-testid="receipt-remove-btn"
              >
                <X size={14} color={COLORS.error} />
              </button>
            </div>
          ) : (
            // G2: Receipt Empty State
            <div style={styles.receiptEmptyState}>
              <div style={styles.receiptIcon}>
                <Cloud size={20} color={COLORS.gold} />
              </div>
              <div style={styles.receiptText}>Tap to attach receipt</div>
              <div style={styles.receiptMeta}>Image or PDF, max 10MB</div>
            </div>
          )}
        </div>

        {/* Backend-dependent notice for receipt upload */}
        {receiptFile && (
          <div style={{ 
            marginTop: 8, 
            padding: '8px 12px', 
            borderRadius: 8, 
            background: COLORS.warningLight,
            fontSize: 11,
            color: '#92400e',
          }}>
            <strong>Note:</strong> Receipt upload requires /api/finance/entries/:id/receipt endpoint (backend-dependent)
          </div>
        )}

        {/* F16 & F17: Action Buttons */}
        <div style={styles.entryButtonRow}>
          {/* F16: Cancel Button */}
          <button
            style={styles.cancelBtn}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            data-testid="entry-cancel-btn"
          >
            Cancel
          </button>
          
          {/* F17: Save Entry Button */}
          <button
            style={{
              ...styles.saveEntryBtn,
              ...(!isFormValid || isSaving ? styles.saveEntryBtnDisabled : {}),
            }}
            onClick={handleSave}
            disabled={!isFormValid || isSaving}
            onMouseEnter={(e) => {
              if (isFormValid && !isSaving) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
            }}
            data-testid="entry-save-btn"
          >
            {/* F18: Saving State */}
            {isSaving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </div>
      
      {/* CSS Animation */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 4: MASTER LEDGER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── K1-K7, L1-L6, M1-M10: Vendor Control Panel ─────────────────────────────────

function VendorControlPanel({ entry, onFlag, onPause, onCancelReplace, onAddJournal, onRemoveJournal }) {
  const [reason, setReason] = useState('');
  const [journalInput, setJournalInput] = useState('');
  const [isLoading, setIsLoading] = useState(null); // 'flag' | 'pause' | 'cancel'
  const [showCRFlow, setShowCRFlow] = useState(false);
  // Cancel & Replace form state
  const [crReason, setCRReason] = useState('');
  const [createReplacement, setCreateReplacement] = useState(true);
  const [replacementLabel, setReplacementLabel] = useState('');
  const [replacementVendor, setReplacementVendor] = useState('');
  const [replacementAmount, setReplacementAmount] = useState('');
  const [replacementNotes, setReplacementNotes] = useState('');

  // K4: Flag handler
  const handleFlag = async () => {
    setIsLoading('flag');
    try {
      if (onFlag) await onFlag(entry, reason);
    } finally {
      setIsLoading(null);
      setReason('');
    }
  };

  // K5: Pause handler
  const handlePause = async () => {
    setIsLoading('pause');
    try {
      if (onPause) await onPause(entry, reason);
    } finally {
      setIsLoading(null);
      setReason('');
    }
  };

  // K6: Cancel+Replace handler - show flow
  const handleShowCRFlow = () => {
    setShowCRFlow(true);
    // Pre-fill with original entry values
    setReplacementLabel(entry.label || '');
    setReplacementVendor(entry.vendor_name || '');
    setReplacementAmount(entry.contract_amount?.toString() || '');
  };

  // Execute Cancel & Replace
  const handleExecuteCancelReplace = async () => {
    if (!crReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    setIsLoading('cancel');
    try {
      if (onCancelReplace) {
        await onCancelReplace(entry, {
          reason: crReason.trim(),
          createReplacement,
          label: replacementLabel.trim() || undefined,
          vendor_name: replacementVendor.trim() || undefined,
          contract_amount: replacementAmount ? parseFloat(replacementAmount) : undefined,
          notes: replacementNotes.trim() || undefined,
          category: entry.category,
          due_date: entry.due_date,
        });
      }
      // Reset form on success
      setShowCRFlow(false);
      setCRReason('');
      setCreateReplacement(true);
      setReplacementLabel('');
      setReplacementVendor('');
      setReplacementAmount('');
      setReplacementNotes('');
    } finally {
      setIsLoading(null);
    }
  };

  // L5: Add journal handler
  const handleAddJournal = () => {
    if (!journalInput.trim()) return;
    if (onAddJournal) {
      onAddJournal(entry, journalInput.trim());
    }
    setJournalInput('');
  };

  // L6: Remove journal handler
  const handleRemoveJournal = (noteId) => {
    if (!window.confirm('Remove this journal note?')) return;
    if (onRemoveJournal) {
      onRemoveJournal(entry, noteId);
    }
  };

  // Calculate impact for Cancel+Replace
  const retained = entry.amount_paid || 0;
  const recoverable = Math.round((entry.contract_amount || 0) * 0.15);
  const contingency = Math.round((entry.contract_amount || 0) * 0.05);
  const bufferCovers = recoverable > contingency;

  return (
    // K1: Control Panel Container
    <div style={styles.controlPanel} data-testid="control-panel">
      {/* K2: Reason Input */}
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason / note (optional)"
        style={styles.reasonInput}
        data-testid="control-reason-input"
      />
      
      {/* K3: Control Button Row */}
      <div style={styles.controlBtnRow}>
        {/* K4: Flag Discrepancy Button */}
        <button
          style={{ ...styles.controlBtn, ...styles.controlBtnFlag }}
          onClick={handleFlag}
          disabled={isLoading === 'flag'}
          data-testid="control-flag-btn"
        >
          {/* K7: Button Loading State */}
          {isLoading === 'flag' ? '...' : '🚩'} Flag Discrepancy
        </button>
        
        {/* K5: Pause Service Button */}
        <button
          style={{ ...styles.controlBtn, ...styles.controlBtnPause }}
          onClick={handlePause}
          disabled={isLoading === 'pause'}
          data-testid="control-pause-btn"
        >
          {isLoading === 'pause' ? '...' : '⏸️'} Pause Service
        </button>
        
        {/* K6: Cancel & Replace Button */}
        <button
          style={{ ...styles.controlBtn, ...styles.controlBtnCancel }}
          onClick={handleShowCRFlow}
          disabled={entry.cancelled}
          data-testid="control-cancel-btn"
        >
          {entry.cancelled ? '⊘ Cancelled' : '🔄 Cancel & Replace'}
        </button>
      </div>
      
      {/* M1-M10: Cancel + Replace Flow - Real Form */}
      {showCRFlow && (
        <div style={styles.crFlowContainer} data-testid="cr-flow">
          {/* M2: CR Title */}
          <div style={styles.crTitle}>Cancel & Replace Entry</div>
          
          {/* Impact Summary */}
          <div style={styles.impactGrid}>
            <div style={styles.impactCell}>
              <div style={styles.impactLabel}>Already Paid</div>
              <div style={{ ...styles.impactValue, ...styles.impactValueRed }}>
                {fmt(retained)}
              </div>
            </div>
            <div style={styles.impactCell}>
              <div style={styles.impactLabel}>Contract Total</div>
              <div style={styles.impactValue}>
                {fmt(entry.contract_amount || 0)}
              </div>
            </div>
          </div>
          
          {/* Cancellation Reason - REQUIRED */}
          <div style={{ marginBottom: 12 }}>
            <label style={styles.impactLabel}>Reason for Cancellation *</label>
            <input
              type="text"
              value={crReason}
              onChange={(e) => setCRReason(e.target.value)}
              placeholder="Why is this being cancelled?"
              style={{ ...styles.reasonInput, width: '100%', marginTop: 4 }}
              data-testid="cr-reason-input"
            />
          </div>
          
          {/* Create Replacement Toggle */}
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={createReplacement}
              onChange={(e) => setCreateReplacement(e.target.checked)}
              id="create-replacement"
              data-testid="cr-create-replacement"
            />
            <label htmlFor="create-replacement" style={{ color: COLORS.textSecondary, fontSize: 14 }}>
              Create replacement entry
            </label>
          </div>
          
          {/* Replacement Form - only shown if createReplacement is true */}
          {createReplacement && (
            <div style={{ background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 8, marginBottom: 12 }}>
              <div style={{ ...styles.impactLabel, marginBottom: 8 }}>Replacement Details</div>
              
              <input
                type="text"
                value={replacementLabel}
                onChange={(e) => setReplacementLabel(e.target.value)}
                placeholder="Label (e.g., New Catering Service)"
                style={{ ...styles.reasonInput, width: '100%', marginBottom: 8 }}
                data-testid="cr-replacement-label"
              />
              
              <input
                type="text"
                value={replacementVendor}
                onChange={(e) => setReplacementVendor(e.target.value)}
                placeholder="Vendor Name"
                style={{ ...styles.reasonInput, width: '100%', marginBottom: 8 }}
                data-testid="cr-replacement-vendor"
              />
              
              <input
                type="number"
                value={replacementAmount}
                onChange={(e) => setReplacementAmount(e.target.value)}
                placeholder="Contract Amount"
                style={{ ...styles.reasonInput, width: '100%', marginBottom: 8 }}
                data-testid="cr-replacement-amount"
              />
              
              <textarea
                value={replacementNotes}
                onChange={(e) => setReplacementNotes(e.target.value)}
                placeholder="Notes (optional)"
                style={{ ...styles.reasonInput, width: '100%', minHeight: 60, resize: 'vertical' }}
                data-testid="cr-replacement-notes"
              />
            </div>
          )}
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...styles.controlBtn, ...styles.controlBtnCancel, flex: 1 }}
              onClick={handleExecuteCancelReplace}
              disabled={isLoading === 'cancel' || !crReason.trim()}
              data-testid="cr-execute-btn"
            >
              {isLoading === 'cancel' ? 'Processing...' : (createReplacement ? 'Cancel & Create Replacement' : 'Cancel Entry')}
            </button>
            <button
              style={styles.crCloseBtn}
              onClick={() => setShowCRFlow(false)}
              data-testid="cr-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* L1-L6: Internal Journal */}
      <div style={styles.journalSection} data-testid="journal-section">
        {/* L2: Journal Title */}
        <div style={styles.journalTitle}>Internal Journal</div>
        
        {/* L3: Journal Note Cards */}
        {(entry.journal || []).map((note, idx) => (
          <div key={note.id || idx} style={styles.journalNote}>
            <div style={{ flex: 1 }}>
              <div style={styles.journalNoteText}>{note.text}</div>
              <div style={styles.journalNoteDate}>
                {new Date(note.date || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <button
              style={styles.journalRemoveBtn}
              onClick={() => handleRemoveJournal(note.id || idx)}
            >
              <X size={12} color={COLORS.textMuted} />
            </button>
          </div>
        ))}
        
        {/* L4 & L5: Journal Input Row */}
        <div style={styles.journalInputRow}>
          <input
            type="text"
            value={journalInput}
            onChange={(e) => setJournalInput(e.target.value)}
            placeholder="Add a note..."
            style={styles.journalInput}
            onKeyPress={(e) => e.key === 'Enter' && handleAddJournal()}
            data-testid="journal-input"
          />
          <button
            style={styles.journalAddBtn}
            onClick={handleAddJournal}
            data-testid="journal-add-btn"
          >
            <Plus size={16} color={COLORS.white} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── J1-J9: Expense Ledger Row ──────────────────────────────────────────────────

function ExpenseLedgerRow({ entry, isExpanded, onToggle, onFlag, onPause, onCancelReplace, onAddJournal, onRemoveJournal, onEdit, onDelete, allEntries = [], orchestrationAwareness = null, onNavigateToReadiness = null }) {
  const [isHovering, setIsHovering] = useState(false);
  
  const isFlagged = entry.status === 'flagged' || entry.flagged;
  const needsReview = entry.needs_review;
  const isCancelled = entry.cancelled || entry.status === 'cancelled';
  
  // Find replacement entry if this entry has one
  const replacementEntry = entry.replaced_by ? allEntries.find(e => e._id === entry.replaced_by || e.id === entry.replaced_by) : null;
  // Find original entry if this is a replacement
  const originalEntry = entry.replaces ? allEntries.find(e => e._id === entry.replaces || e.id === entry.replaces) : null;
  
  // J8: Status styling
  const getStatusStyle = () => {
    if (isCancelled) return styles.statusCancelled;
    switch (entry.status) {
      case 'active': return styles.statusActive;
      case 'urgent': return styles.statusUrgent;
      case 'paused': return styles.statusPaused;
      case 'archived': return styles.statusArchived;
      default: return styles.statusActive;
    }
  };

  // Handle delete click
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Delete expense "${entry.label || entry.name}"? This cannot be undone.`)) {
      if (onDelete) onDelete(entry);
    }
  };

  // Handle edit click
  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(entry);
  };

  return (
    <>
      {/* J1-J3: Ledger Row Container with conditional styling */}
      <div
        style={{
          ...styles.ledgerRow,
          ...(isHovering ? styles.ledgerRowHover : {}),
          ...(isExpanded ? styles.ledgerRowAmber : {}),
          ...(isFlagged ? styles.ledgerRowRed : {}),
          ...(isCancelled ? { opacity: 0.6, background: 'rgba(255, 107, 107, 0.05)' } : {}),
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={onToggle}
        data-testid={`expense-row-${entry._id || entry.id}`}
      >
        {/* J4 & J5: Label Cell */}
        <div style={styles.colLabel}>
          <div style={{ ...styles.cellPrimary, ...(isCancelled ? { textDecoration: 'line-through' } : {}), display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>{entry.label || entry.name}</span>
            {orchestrationAwareness && orchestrationAwareness.count > 0 && (
              <OrchestrationIndicator
                count={orchestrationAwareness.count}
                titles={orchestrationAwareness.titles}
                opIds={orchestrationAwareness.op_ids}
                onNavigate={onNavigateToReadiness}
                ariaLabel={`${orchestrationAwareness.count} open ${orchestrationAwareness.count === 1 ? 'operation' : 'operations'} in Event Readiness for ${entry.label}`}
              />
            )}
          </div>
          {entry.vendor_name && (
            <div style={styles.cellSub}>{entry.vendor_name}</div>
          )}
          {/* Show replacement link if cancelled */}
          {isCancelled && replacementEntry && (
            <div style={{ fontSize: 11, color: COLORS.accentBlue, marginTop: 2 }}>
              ↳ Replaced by: {replacementEntry.label}
            </div>
          )}
          {/* Show original link if this is a replacement */}
          {originalEntry && (
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              ↱ Replaces: {originalEntry.label}
            </div>
          )}
        </div>
        
        {/* Category */}
        <div style={styles.colCategory}>
          <span style={styles.cellSub}>{entry.category || '—'}</span>
        </div>
        
        {/* J6: Amount */}
        <div style={{ ...styles.colAmount, textAlign: 'right' }}>
          <span style={styles.cellPrimary}>{fmt(entry.contract_amount)}</span>
        </div>
        
        {/* J6: Paid (green) */}
        <div style={{ ...styles.colPaid, textAlign: 'right' }}>
          <span style={{ ...styles.cellPrimary, ...styles.amountGreen }}>
            {fmt(entry.amount_paid || 0)}
          </span>
        </div>
        
        {/* Due Date */}
        <div style={styles.colDue}>
          <span style={styles.cellSub}>
            {entry.due_date ? new Date(entry.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
          </span>
        </div>
        
        {/* Method */}
        <div style={styles.colMethod}>
          <span style={styles.cellSub}>{entry.payment_method || '—'}</span>
        </div>
        
        {/* J7 & J8: Status */}
        <div style={styles.colStatus}>
          {needsReview ? (
            // J7: Needs Review Badge
            <span style={styles.needsReviewBadge}>
              <span style={styles.needsReviewDot} />
              Needs Review
            </span>
          ) : (
            // J8: Status Badge - with cancelled support
            <span style={{ ...styles.statusBadge, ...getStatusStyle() }}>
              <span style={{ ...styles.statusDot, background: 'currentColor' }} />
              {isCancelled ? 'CANCELLED' : (entry.status || 'Active')}
            </span>
          )}
        </div>
        
        {/* J9: Action Buttons (Note, Edit, Delete) */}
        <div style={{ ...styles.colActions, textAlign: 'center', display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            style={styles.noteBtn}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            title="Notes & Controls"
            data-testid={`note-btn-${entry._id || entry.id}`}
          >
            <MessageSquare size={14} color={isExpanded ? COLORS.gold : COLORS.textMuted} />
          </button>
          
          {/* Edit Button */}
          <button
            style={styles.actionBtn}
            onClick={handleEdit}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            title="Edit Expense"
            data-testid={`edit-expense-btn-${entry._id || entry.id}`}
          >
            <Pencil size={12} color={COLORS.textMuted} />
          </button>
          
          {/* Delete Button */}
          <button
            style={styles.actionBtn}
            onClick={handleDelete}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.errorLight;
              e.currentTarget.style.borderColor = COLORS.error;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            title="Delete Expense"
            data-testid={`delete-expense-btn-${entry._id || entry.id}`}
          >
            <Trash2 size={12} color={COLORS.error} />
          </button>
        </div>
      </div>
      
      {/* K1-M10: Vendor Control Panel (when expanded) */}
      {isExpanded && (
        <VendorControlPanel
          entry={entry}
          onFlag={onFlag}
          onPause={onPause}
          onCancelReplace={onCancelReplace}
          onAddJournal={onAddJournal}
          onRemoveJournal={onRemoveJournal}
        />
      )}
    </>
  );
}

// ─── O1-O10: Sponsor Ledger Row ─────────────────────────────────────────────────

function SponsorLedgerRow({ sponsor, onAddPayment, onRemovePayment, onEdit, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', date: '', method: '', note: '' });

  const pledge = sponsor.pledge_amount || sponsor.amount || 0;
  const received = sponsor.amount_received || sponsor.received || 0;
  const remaining = pledge - received;
  const payments = sponsor.payments || [];

  // O9: Add payment handler
  const handleAddPayment = () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) return;
    
    if (onAddPayment) {
      onAddPayment(sponsor, {
        amount: parseFloat(newPayment.amount),
        date: newPayment.date || new Date().toISOString().split('T')[0],
        method: newPayment.method || 'Check',
        note: newPayment.note || '',
      });
    }
    setNewPayment({ amount: '', date: '', method: '', note: '' });
    setShowAddForm(false);
  };

  // O10: Remove payment handler
  const handleRemovePayment = (paymentId) => {
    if (!window.confirm('Remove this payment record?')) return;
    if (onRemovePayment) {
      onRemovePayment(sponsor, paymentId);
    }
  };

  // Handle delete click
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Delete sponsor "${sponsor.name}"? This cannot be undone.`)) {
      if (onDelete) onDelete(sponsor);
    }
  };

  // Handle edit click
  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(sponsor);
  };

  return (
    <>
      {/* O1: Sponsor Row Container */}
      <div
        style={styles.ledgerRow}
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid={`sponsor-row-${sponsor._id || sponsor.id}`}
      >
        {/* Name */}
        <div style={styles.colLabel}>
          <div style={styles.cellPrimary}>{sponsor.name}</div>
        </div>
        
        {/* O2: Sponsor Type Badge */}
        <div style={styles.colCategory}>
          <span style={{
            padding: '2px 8px',
            borderRadius: 4,
            border: `1px solid ${COLORS.goldBorder}`,
            fontSize: 10,
            fontWeight: 600,
            color: COLORS.textMuted,
          }}>
            {sponsor.type || 'Individual'}
          </span>
        </div>
        
        {/* O3: Pledge */}
        <div style={{ ...styles.colAmount, textAlign: 'right' }}>
          <span style={styles.cellPrimary}>{fmt(pledge)}</span>
        </div>
        
        {/* O3: Received (green) */}
        <div style={{ ...styles.colPaid, textAlign: 'right' }}>
          <span style={{ ...styles.cellPrimary, ...styles.amountGreen }}>
            {fmt(received)}
          </span>
        </div>
        
        {/* O3: Remaining */}
        <div style={{ ...styles.colDue, textAlign: 'right' }}>
          <span style={{ ...styles.cellPrimary, color: remaining > 0 ? COLORS.amber : COLORS.successLight }}>
            {fmt(remaining)}
          </span>
        </div>
        
        {/* Expected Date */}
        <div style={{ ...styles.colMethod, paddingLeft: 6 }}>
          <span style={styles.cellSub}>
            {sponsor.expected_date ? new Date(sponsor.expected_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
          </span>
        </div>
        
        {/* Spacer for status column */}
        <div style={styles.colStatus} />
        
        {/* O4: Action Buttons (Expand, Edit, Delete) */}
        <div style={{ ...styles.colActions, textAlign: 'center', display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            style={styles.expandBtn}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            title="Expand Payments"
            data-testid={`expand-btn-${sponsor._id || sponsor.id}`}
          >
            {isExpanded ? (
              <ChevronUp size={14} color={COLORS.gold} />
            ) : (
              <ChevronDown size={14} color={COLORS.textMuted} />
            )}
          </button>
          
          {/* Edit Button */}
          <button
            style={styles.actionBtn}
            onClick={handleEdit}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            title="Edit Sponsor"
            data-testid={`edit-sponsor-btn-${sponsor._id || sponsor.id}`}
          >
            <Pencil size={12} color={COLORS.textMuted} />
          </button>
          
          {/* Delete Button */}
          <button
            style={styles.actionBtn}
            onClick={handleDelete}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.errorLight;
              e.currentTarget.style.borderColor = COLORS.error;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            title="Delete Sponsor"
            data-testid={`delete-sponsor-btn-${sponsor._id || sponsor.id}`}
          >
            <Trash2 size={12} color={COLORS.error} />
          </button>
        </div>
      </div>
      
      {/* O5-O10: Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* O5: Sub-Rows Container */}
          <div style={styles.subRowsContainer}>
            {payments.length === 0 ? (
              // O6: No Payments Text
              <div style={styles.noPaymentsText}>No payments recorded yet</div>
            ) : (
              // O7: Payment Sub-Rows
              payments.map((payment, idx) => (
                <div key={payment.id || idx} style={styles.paymentSubRow}>
                  <span style={styles.paymentAmount}>{fmt(payment.amount)}</span>
                  <span style={styles.paymentMeta}>
                    {payment.method || 'Check'} · {new Date(payment.date || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {payment.note && ` · ${payment.note}`}
                  </span>
                  <button
                    style={styles.paymentRemoveBtn}
                    onClick={() => handleRemovePayment(payment.id || idx)}
                  >
                    <Trash2 size={12} color={COLORS.textMuted} />
                  </button>
                </div>
              ))
            )}
            
            {/* O8: Add Payment Button */}
            {!showAddForm && (
              <button
                style={styles.addPaymentBtn}
                onClick={() => setShowAddForm(true)}
                data-testid={`add-payment-btn-${sponsor._id || sponsor.id}`}
              >
                <Plus size={14} />
                Add Payment
              </button>
            )}
            
            {/* O9: Add Payment Form */}
            {showAddForm && (
              <div style={styles.addPaymentForm}>
                <input
                  type="number"
                  placeholder="Amount"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  style={styles.addPaymentInput}
                  data-testid="payment-amount-input"
                />
                <input
                  type="date"
                  value={newPayment.date}
                  onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                  style={styles.addPaymentInput}
                  data-testid="payment-date-input"
                />
                <select
                  value={newPayment.method}
                  onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                  style={styles.addPaymentInput}
                >
                  <option value="">Method</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Wire">Wire</option>
                  <option value="Zelle">Zelle</option>
                </select>
                <div style={styles.addPaymentBtnRow}>
                  <button
                    style={styles.addPaymentCancel}
                    onClick={() => {
                      setShowAddForm(false);
                      setNewPayment({ amount: '', date: '', method: '', note: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.addPaymentSubmit}
                    onClick={handleAddPayment}
                    data-testid="payment-submit-btn"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── H1-H7, I1-I3, N1-N2: Master Ledger Component ───────────────────────────────

function MasterLedger({ expenses, sponsors, onFlag, onPause, onCancelReplace, onAddJournal, onRemoveJournal, onAddSponsorPayment, onRemoveSponsorPayment, onEditExpense, onDeleteExpense, onEditSponsor, onDeleteSponsor, orchestrationAwareness = {}, onNavigateToReadiness = null }) {
  // H3 & H4: Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);

  // H4: Filter logic
  const filteredExpenses = expenses.filter(entry => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (entry.label || entry.name || '').toLowerCase().includes(q) ||
      (entry.category || '').toLowerCase().includes(q) ||
      (entry.payment_method || '').toLowerCase().includes(q) ||
      String(entry.contract_amount || '').includes(q)
    );
  });

  const filteredSponsors = sponsors.filter(sponsor => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (sponsor.name || '').toLowerCase().includes(q) ||
      (sponsor.type || '').toLowerCase().includes(q) ||
      String(sponsor.pledge_amount || sponsor.amount || '').includes(q)
    );
  });

  const totalEntries = filteredExpenses.length + filteredSponsors.length;
  const isEmpty = expenses.length === 0 && sponsors.length === 0;

  return (
    // H1: Ledger Section Container
    <div style={styles.ledgerSection} data-testid="master-ledger">
      {/* H2: Ledger Section Header */}
      <div style={styles.ledgerHeader}>
        <div>
          <span style={styles.ledgerTitle}>Master Ledger</span>
          <span style={styles.ledgerCount}>({totalEntries} entries)</span>
        </div>
        
        {/* H3: Search Bar */}
        <div style={styles.searchBar}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search entries..."
            style={styles.searchInput}
            data-testid="ledger-search-input"
          />
          {searchQuery && (
            <button
              style={styles.searchClear}
              onClick={() => setSearchQuery('')}
            >
              <X size={14} color={COLORS.textMuted} />
            </button>
          )}
        </div>
      </div>
      
      {isEmpty ? (
        // H5: Empty State
        <div style={styles.ledgerEmptyState} data-testid="ledger-empty">
          <div style={styles.ledgerEmptyIcon}>
            <Receipt size={28} color={COLORS.gold} />
          </div>
          <div style={styles.ledgerEmptyTitle}>No entries yet</div>
          <div style={styles.ledgerEmptyText}>
            Add your first finance entry using the button above
          </div>
        </div>
      ) : (
        // H6: Table Card
        <div style={styles.tableCard}>
          {/* H7: Table Frame */}
          <div style={styles.tableFrame}>
            {/* ═══ I1-I3: EXPENSE SECTION ════════════════════════════════════ */}
            {filteredExpenses.length > 0 && (
              <>
                {/* I1: Expense Sub-Header */}
                <div style={styles.sectionSubHeader}>
                  <span style={styles.sectionSubHeaderText}>
                    EXPENSES ({filteredExpenses.length})
                  </span>
                </div>
                
                {/* I2 & I3: Expense Header Row */}
                <div style={styles.tableHeaderRow}>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colLabel }} title="What the expense is — the vendor or a short description.">Label</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colCategory }} title="The budget category this expense falls under.">Category</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colAmount, textAlign: 'right' }} title="The total (contract) amount of this expense.">Amount</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colPaid, textAlign: 'right' }} title="How much has been paid toward it so far.">Paid</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colDue }} title="When the remaining balance is due.">Due Date</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colMethod }} title="How it's being paid — card, transfer, cash, etc.">Method</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colStatus }} title="Where this entry stands — e.g. needs review, scheduled, or paid.">Status</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colActions }}></div>
                </div>
                
                {/* J1-J9: Expense Rows */}
                {filteredExpenses.map(entry => (
                  <ExpenseLedgerRow
                    key={entry._id || entry.id}
                    entry={entry}
                    allEntries={expenses}
                    isExpanded={expandedExpenseId === (entry._id || entry.id)}
                    onToggle={() => setExpandedExpenseId(
                      expandedExpenseId === (entry._id || entry.id) ? null : (entry._id || entry.id)
                    )}
                    onFlag={onFlag}
                    onPause={onPause}
                    onCancelReplace={onCancelReplace}
                    onAddJournal={onAddJournal}
                    onRemoveJournal={onRemoveJournal}
                    onEdit={onEditExpense}
                    onDelete={onDeleteExpense}
                    orchestrationAwareness={orchestrationAwareness[entry._id || entry.id] || null}
                    onNavigateToReadiness={onNavigateToReadiness}
                  />
                ))}
              </>
            )}
            
            {/* ═══ N1-N2, O1-O10: SPONSOR SECTION ════════════════════════════ */}
            {filteredSponsors.length > 0 && (
              <>
                {/* N1: Sponsor Sub-Header */}
                <div style={styles.sectionSubHeader}>
                  <span style={styles.sectionSubHeaderText}>
                    SPONSORS ({filteredSponsors.length})
                  </span>
                </div>
                
                {/* N2: Sponsor Header Row */}
                <div style={styles.tableHeaderRow}>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colLabel }} title="The sponsor or donor's name.">Name</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colCategory }} title="How this sponsor is categorized — e.g. Individual or Family.">Type</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colAmount, textAlign: 'right' }} title="The total amount this sponsor promised to give.">Pledge</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colPaid, textAlign: 'right' }} title="How much of the pledge has actually been paid so far.">Received</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colDue, textAlign: 'right' }} title="How much of the pledge is still owed — Pledge minus Received.">Remaining</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colMethod, paddingLeft: 6 }} title="The date you expect the remaining pledge to arrive.">Expected</div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colStatus }}></div>
                  <div style={{ ...styles.tableHeaderCell, ...styles.colActions }}></div>
                </div>
                
                {/* O1-O10: Sponsor Rows */}
                {filteredSponsors.map(sponsor => (
                  <SponsorLedgerRow
                    key={sponsor._id || sponsor.id}
                    sponsor={sponsor}
                    onAddPayment={onAddSponsorPayment}
                    onRemovePayment={onRemoveSponsorPayment}
                    onEdit={onEditSponsor}
                    onDelete={onDeleteSponsor}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 5: EXPORT ZONE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── P1-P8: Export Zone Component ───────────────────────────────────────────────

function ExportZone({ expenses, sponsors, budget }) {
  const [showTextPreview, setShowTextPreview] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Generate iCal URL (backend serves at /api/calendar/feed.ics)
  const publicSiteUrl = process.env.REACT_APP_PUBLIC_SITE_URL || API;
  const icalUrl = `${publicSiteUrl}/api/calendar/feed.ics`;
  
  // Generate text export content
  const generateTextExport = () => {
    const lines = [];
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('                    FINANCE EXPORT REPORT                   ');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');
    
    // Budget Summary
    const totalBudget = budget?.total_budget ?? 0;
    const totalSpent = expenses.reduce((sum, e) => sum + (e.amount_paid || 0), 0);
    const totalCommitted = expenses.reduce((sum, e) => sum + (e.contract_amount || 0), 0);
    
    lines.push('───────────────────────────────────────────────────────────');
    lines.push('  BUDGET SUMMARY');
    lines.push('───────────────────────────────────────────────────────────');
    lines.push(`  Total Budget:      ${fmt(totalBudget)}`);
    lines.push(`  Total Committed:   ${fmt(totalCommitted)}`);
    lines.push(`  Total Spent:       ${fmt(totalSpent)}`);
    lines.push(`  Remaining:         ${fmt(totalBudget - totalSpent)}`);
    lines.push('');
    
    // Expenses
    if (expenses.length > 0) {
      lines.push('───────────────────────────────────────────────────────────');
      lines.push('  EXPENSES');
      lines.push('───────────────────────────────────────────────────────────');
      expenses.forEach((e, idx) => {
        lines.push(`  ${idx + 1}. ${e.label || e.name}`);
        lines.push(`     Contract: ${fmt(e.contract_amount)} | Paid: ${fmt(e.amount_paid || 0)}`);
        if (e.due_date) lines.push(`     Due: ${new Date(e.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
        if (e.category) lines.push(`     Category: ${e.category}`);
        lines.push('');
      });
    }
    
    // Sponsors
    if (sponsors.length > 0) {
      const totalPledged = sponsors.reduce((sum, s) => sum + (s.pledge_amount || s.amount || 0), 0);
      const totalReceived = sponsors.reduce((sum, s) => sum + (s.amount_received || s.received || 0), 0);
      
      lines.push('───────────────────────────────────────────────────────────');
      lines.push('  SPONSORS');
      lines.push('───────────────────────────────────────────────────────────');
      lines.push(`  Total Pledged:     ${fmt(totalPledged)}`);
      lines.push(`  Total Received:    ${fmt(totalReceived)}`);
      lines.push('');
      sponsors.forEach((s, idx) => {
        const pledge = s.pledge_amount || s.amount || 0;
        const received = s.amount_received || s.received || 0;
        lines.push(`  ${idx + 1}. ${s.name} (${s.type || 'Individual'})`);
        lines.push(`     Pledge: ${fmt(pledge)} | Received: ${fmt(received)}`);
        lines.push('');
      });
    }
    
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('                      END OF REPORT                        ');
    lines.push('═══════════════════════════════════════════════════════════');
    
    return lines.join('\n');
  };

  // P3: Preview text export
  const handlePreviewText = () => {
    setShowTextPreview(true);
  };

  // P4: Open PDF report (backend-dependent) - uses fetch + blob for reliable download
  const handleOpenPDF = async () => {
    try {
      toast.info('Generating PDF report...');
      const response = await fetch(`${API}/api/finance/export/pdf`);
      if (!response.ok) {
        throw new Error('PDF generation failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to download PDF');
    }
  };

  // P8: Copy iCal URL
  const handleCopyIcal = async () => {
    try {
      await navigator.clipboard.writeText(icalUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download text export
  const handleDownloadText = () => {
    const content = generateTextExport();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-export-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* P1: Export Zone Container */}
      <div style={styles.exportZone} data-testid="export-zone">
        <div style={styles.exportHeader}>
          <div style={styles.exportTitle}>Export & Subscriptions</div>
          <div style={styles.exportSubtitle}>Download reports or subscribe to calendar updates</div>
        </div>
        
        {/* P2: Export Button Row */}
        <div style={styles.exportButtonRow}>
          {/* P3: Preview Text Export Button */}
          <button
            style={styles.exportBtnOutline}
            onClick={handlePreviewText}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            data-testid="export-preview-btn"
          >
            <FileText size={16} />
            Preview Text Export
          </button>
          
          {/* P4: Open PDF Report Button */}
          <button
            style={styles.exportBtnSolid}
            onClick={handleOpenPDF}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
            }}
            data-testid="export-pdf-btn"
          >
            <Download size={16} />
            Open PDF Report
          </button>
        </div>
        
        {/* P5: Export Note */}
        <div style={styles.exportNote}>
          PDF export uses live backend data from MongoDB
        </div>
        
        {/* P6: iCal Info Row */}
        <div style={styles.icalRow} data-testid="ical-row">
          <div style={styles.icalIcon}>
            <Calendar size={18} color={COLORS.gold} />
          </div>
          <div style={styles.icalInfo}>
            <div style={styles.icalLabel}>iCal Subscription URL</div>
            {/* P7: iCal URL Link */}
            <a
              href={icalUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.icalUrl}
              data-testid="ical-url"
            >
              {icalUrl}
            </a>
          </div>
          {/* P8: iCal Copy Button */}
          <button
            style={styles.icalCopyBtn}
            onClick={handleCopyIcal}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = COLORS.goldBorder;
            }}
            data-testid="ical-copy-btn"
          >
            {copySuccess ? (
              <Check size={16} color={COLORS.successLight} />
            ) : (
              <Copy size={16} color={COLORS.textMuted} />
            )}
          </button>
        </div>
      </div>
      
      {/* Text Preview Modal */}
      {showTextPreview && (
        <div 
          style={styles.textPreviewModal} 
          onClick={() => setShowTextPreview(false)}
          data-testid="text-preview-modal"
        >
          <div style={styles.textPreviewCard} onClick={e => e.stopPropagation()}>
            <div style={styles.textPreviewHeader}>
              <span style={styles.textPreviewTitle}>Finance Export Preview</span>
              <button
                style={styles.entryStripClose}
                onClick={() => setShowTextPreview(false)}
              >
                <X size={16} color={COLORS.textMuted} />
              </button>
            </div>
            <div style={styles.textPreviewBody}>
              <pre style={styles.textPreviewContent}>
                {generateTextExport()}
              </pre>
            </div>
            <div style={styles.textPreviewFooter}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowTextPreview(false)}
              >
                Close
              </button>
              <button
                style={styles.saveEntryBtn}
                onClick={handleDownloadText}
              >
                <Download size={14} style={{ marginRight: 6 }} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT MODAL COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Edit Expense Modal ─────────────────────────────────────────────────────────

function EditExpenseModal({ expense, onClose, onSave }) {
  const [label, setLabel] = useState(expense.label || expense.name || '');
  const [vendorName, setVendorName] = useState(expense.vendor_name || '');
  const [contractAmount, setContractAmount] = useState(expense.contract_amount || 0);
  const [amountPaid, setAmountPaid] = useState(expense.amount_paid || 0);
  const [dueDate, setDueDate] = useState(expense.due_date ? expense.due_date.split('T')[0] : '');
  const [category, setCategory] = useState(expense.category || '');
  const [paymentMethod, setPaymentMethod] = useState(expense.payment_method || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!label.trim() || !contractAmount) return;
    setIsSaving(true);
    try {
      const updatedExpense = {
        ...expense,
        label: label.trim(),
        vendor_name: vendorName.trim() || null,
        contract_amount: parseFloat(contractAmount),
        amount_paid: parseFloat(amountPaid) || 0,
        due_date: dueDate || null,
        category: category || null,
        payment_method: paymentMethod || null,
      };
      onSave(updatedExpense);
      onClose();
      toast.success(`Expense "${label}" updated`);
    } catch (err) {
      console.error('Failed to save expense:', err);
      toast.error('Failed to save expense');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={styles.textPreviewModal} onClick={onClose} data-testid="edit-expense-modal">
      <div style={{ ...styles.textPreviewCard, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={styles.textPreviewHeader}>
          <span style={styles.textPreviewTitle}>Edit Expense</span>
          <button style={styles.entryStripClose} onClick={onClose}>
            <X size={16} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: 20 }}>
          {/* Label */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Label / Vendor</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={styles.formInput}
              data-testid="edit-expense-label"
            />
          </div>
          
          {/* Vendor Name */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Vendor Name</label>
            <input
              type="text"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              style={styles.formInput}
              data-testid="edit-expense-vendor"
            />
          </div>
          
          {/* Amount Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Contract Amount</label>
              <input
                type="number"
                value={contractAmount}
                onChange={(e) => setContractAmount(e.target.value)}
                style={styles.formInput}
                data-testid="edit-expense-amount"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Amount Paid</label>
              <input
                type="number"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                style={styles.formInput}
                data-testid="edit-expense-paid"
              />
            </div>
          </div>
          
          {/* Due Date */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={styles.formInput}
              data-testid="edit-expense-date"
            />
          </div>
          
          {/* Category */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Category</label>
            <div style={styles.categoryChipsRow}>
              {CATEGORY_OPTIONS.map(cat => (
                <button
                  key={cat}
                  style={{
                    ...styles.categoryChip,
                    ...(category === cat ? styles.categoryChipActive : {}),
                  }}
                  onClick={() => setCategory(category === cat ? '' : cat)}
                  type="button"
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          {/* Payment Method */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Payment Method</label>
            <div style={styles.categoryChipsRow}>
              {PAYMENT_METHOD_OPTIONS.map(method => (
                <button
                  key={method}
                  style={{
                    ...styles.categoryChip,
                    ...(paymentMethod === method ? styles.categoryChipActive : {}),
                  }}
                  onClick={() => setPaymentMethod(paymentMethod === method ? '' : method)}
                  type="button"
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div style={styles.textPreviewFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{
              ...styles.saveEntryBtn,
              ...(!label.trim() || !contractAmount || isSaving ? styles.saveEntryBtnDisabled : {}),
            }}
            onClick={handleSave}
            disabled={!label.trim() || !contractAmount || isSaving}
            data-testid="edit-expense-save"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Sponsor Modal ─────────────────────────────────────────────────────────

function EditSponsorModal({ sponsor, onClose, onSave }) {
  const [name, setName] = useState(sponsor.name || '');
  const [type, setType] = useState(sponsor.type || 'Individual');
  const [pledgeAmount, setPledgeAmount] = useState(sponsor.pledge_amount || sponsor.amount || 0);
  const [amountReceived, setAmountReceived] = useState(sponsor.amount_received || sponsor.received || 0);
  const [expectedDate, setExpectedDate] = useState(sponsor.expected_date ? sponsor.expected_date.split('T')[0] : '');
  const [isSaving, setIsSaving] = useState(false);

  const SPONSOR_TYPES = ['Individual', 'Family', 'Corporate', 'Foundation', 'Other'];

  const handleSave = async () => {
    if (!name.trim() || !pledgeAmount) return;
    setIsSaving(true);
    try {
      const updatedSponsor = {
        ...sponsor,
        name: name.trim(),
        type: type || 'Individual',
        pledge_amount: parseFloat(pledgeAmount),
        amount_received: parseFloat(amountReceived) || 0,
        expected_date: expectedDate || null,
      };
      onSave(updatedSponsor);
      onClose();
      toast.success(`Sponsor "${name}" updated`);
    } catch (err) {
      console.error('Failed to save sponsor:', err);
      toast.error('Failed to save sponsor');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={styles.textPreviewModal} onClick={onClose} data-testid="edit-sponsor-modal">
      <div style={{ ...styles.textPreviewCard, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={styles.textPreviewHeader}>
          <span style={styles.textPreviewTitle}>Edit Sponsor</span>
          <button style={styles.entryStripClose} onClick={onClose}>
            <X size={16} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: 20 }}>
          {/* Name */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Sponsor Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.formInput}
              data-testid="edit-sponsor-name"
            />
          </div>
          
          {/* Type */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Sponsor Type</label>
            <div style={styles.categoryChipsRow}>
              {SPONSOR_TYPES.map(t => (
                <button
                  key={t}
                  style={{
                    ...styles.categoryChip,
                    ...(type === t ? styles.categoryChipActive : {}),
                  }}
                  onClick={() => setType(t)}
                  type="button"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          
          {/* Amount Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Pledge Amount</label>
              <input
                type="number"
                value={pledgeAmount}
                onChange={(e) => setPledgeAmount(e.target.value)}
                style={styles.formInput}
                data-testid="edit-sponsor-pledge"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Amount Received</label>
              <input
                type="number"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                style={styles.formInput}
                data-testid="edit-sponsor-received"
              />
            </div>
          </div>
          
          {/* Expected Date */}
          <div style={{ ...styles.formGroup, marginBottom: 16 }}>
            <label style={styles.formLabel}>Expected Date</label>
            <input
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              style={styles.formInput}
              data-testid="edit-sponsor-date"
            />
          </div>
        </div>
        
        <div style={styles.textPreviewFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{
              ...styles.saveEntryBtn,
              ...(!name.trim() || !pledgeAmount || isSaving ? styles.saveEntryBtnDisabled : {}),
            }}
            onClick={handleSave}
            disabled={!name.trim() || !pledgeAmount || isSaving}
            data-testid="edit-sponsor-save"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main FinanceTab Component ──────────────────────────────────────────────────

// ─── Event Pricing Rules ────────────────────────────────────────────────────
/* EventPricingRules removed — pricing conditions now live at vendor level */

const FinanceTab = ({ focusEntity = null, onNavigateToReadiness = null }) => {
  // State
  const [budget, setBudget] = useState(null);
  const [vendors, setVendors] = useState([]);
  // Phase 2 — orchestration awareness keyed by finance entry id.
  const [orchestrationAwareness, setOrchestrationAwareness] = useState({});
  
  // Sponsors - NOW BACKEND-BACKED (no more sessionStorage)
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(true);
  
  // Finance entries - BACKEND-BACKED
  const [financeEntries, setFinanceEntries] = useState([]);
  const [financeLoading, setFinanceLoading] = useState(true);
  const [liquidity, setLiquidity] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // REMOVED: sessionStorage persistence for sponsors (now backend-backed)
  
  // A3: Entry form state toggle
  const [showEntryForm, setShowEntryForm] = useState(false);
  
  // Edit modal state
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingSponsor, setEditingSponsor] = useState(null);

  // Fetch finance entries from backend
  const fetchFinanceEntries = useCallback(async () => {
    setFinanceLoading(true);
    try {
      const res = await axios.get(`${API}/api/finance/entries`);
      setFinanceEntries(res.data || []);
    } catch (err) {
      console.error('Failed to fetch finance entries:', err);
      toast.error('Failed to load finance entries');
    } finally {
      setFinanceLoading(false);
    }
  }, []);

  // Fetch sponsors from backend
  const fetchSponsors = useCallback(async () => {
    setSponsorsLoading(true);
    try {
      const res = await axios.get(`${API}/api/sponsors`);
      setSponsors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch sponsors:', err);
      toast.error('Failed to load sponsors');
    } finally {
      setSponsorsLoading(false);
    }
  }, []);

  // Fetch other data (vendors, budget, sponsors, liquidity)
  // Fetch other data (vendors, budget, liquidity)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch vendors (existing endpoint)
      const vendorsRes = await axios.get(`${API}/api/vendors`).catch(() => ({ data: [] }));
      setVendors(vendorsRes.data || []);
      
      // Budget endpoint (backend-dependent - PREVIEW)
      const budgetRes = await axios.get(`${API}/api/budget`).catch(() => ({ data: null }));
      setBudget(budgetRes.data);
      
      // Liquidity endpoint — canonical backend source of truth (GET /api/liquidity).
      // See server.py `_compute_liquidity()` for exact formulas.
      const liquidityRes = await axios.get(`${API}/api/liquidity`).catch(() => ({ data: null }));
      setLiquidity(liquidityRes.data);
      
    } catch (err) {
      console.error('Failed to fetch finance data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all data on mount
  useEffect(() => {
    fetchData();
    fetchFinanceEntries();
    fetchSponsors();
  }, [fetchData, fetchFinanceEntries, fetchSponsors]);

  // Keep the canonical liquidity snapshot (GET /api/liquidity) fresh whenever
  // the underlying money data changes — logging a sponsor payment, editing a
  // sponsor / expense / vendor, etc. Previously liquidity was fetched ONLY on
  // mount, so the "Cash Liquidity" card went stale (kept showing the old number)
  // until a full page reload. Re-pulling here makes the card track money
  // received/spent live, without a manual refresh.
  const refreshLiquidity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/liquidity`);
      setLiquidity(res.data);
    } catch (err) {
      console.error('Failed to refresh liquidity:', err);
    }
  }, []);

  useEffect(() => {
    refreshLiquidity();
  }, [sponsors, financeEntries, vendors, refreshLiquidity]);

  // Phase 2 — orchestration awareness for finance entries
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getModuleAwareness('finance');
        if (!cancelled && data && typeof data === 'object') {
          setOrchestrationAwareness(data);
        }
      } catch (_err) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [financeEntries.length, focusEntity]);

  // Phase 2 — scroll on back-link arrival from Event Readiness
  useEffect(() => {
    if (!focusEntity?.entity_id) return;
    const el = document.querySelector(`[data-testid="expense-row-${focusEntity.entity_id}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusEntity]);

  // ─── FINANCE ENTRY CRUD OPERATIONS (Backend-Backed) ───────────────────────────
  
  const createFinanceEntry = async (entryData) => {
    try {
      const res = await axios.post(`${API}/api/finance/entries`, entryData);
      setFinanceEntries(prev => [res.data, ...prev]);
      toast.success(`Entry "${entryData.label}" created`);
      return res.data;
    } catch (err) {
      console.error('Failed to create finance entry:', err);
      toast.error('Failed to create entry');
      throw err;
    }
  };
  
  const updateFinanceEntry = async (entryId, updates) => {
    try {
      const res = await axios.put(`${API}/api/finance/entries/${entryId}`, updates);
      setFinanceEntries(prev => prev.map(e => 
        (e._id || e.id) === entryId ? res.data : e
      ));
      toast.success('Entry updated');
      return res.data;
    } catch (err) {
      console.error('Failed to update finance entry:', err);
      toast.error('Failed to update entry');
      throw err;
    }
  };
  
  const deleteFinanceEntry = async (entryId, label) => {
    try {
      await axios.delete(`${API}/api/finance/entries/${entryId}`);
      setFinanceEntries(prev => prev.filter(e => 
        (e._id || e.id) !== entryId
      ));
      toast.success(`Expense "${label}" deleted`);
    } catch (err) {
      console.error('Failed to delete finance entry:', err);
      toast.error('Failed to delete entry');
      throw err;
    }
  };

  // ─── SPONSOR CRUD OPERATIONS (Backend-Backed) ─────────────────────────────────
  
  const createSponsor = async (sponsorData) => {
    try {
      const res = await axios.post(`${API}/api/sponsors`, sponsorData);
      setSponsors(prev => [res.data, ...prev]);
      toast.success(`Sponsor "${sponsorData.name}" created`);
      return res.data;
    } catch (err) {
      console.error('Failed to create sponsor:', err);
      toast.error('Failed to create sponsor');
      throw err;
    }
  };
  
  const updateSponsor = async (sponsorId, updates) => {
    try {
      const res = await axios.put(`${API}/api/sponsors/${sponsorId}`, updates);
      setSponsors(prev => prev.map(s => 
        (s._id || s.id) === sponsorId ? res.data : s
      ));
      toast.success('Sponsor updated');
      return res.data;
    } catch (err) {
      console.error('Failed to update sponsor:', err);
      toast.error('Failed to update sponsor');
      throw err;
    }
  };
  
  const deleteSponsor = async (sponsorId, name) => {
    try {
      await axios.delete(`${API}/api/sponsors/${sponsorId}`);
      setSponsors(prev => prev.filter(s => 
        (s._id || s.id) !== sponsorId
      ));
      toast.success(`Sponsor "${name}" deleted`);
    } catch (err) {
      console.error('Failed to delete sponsor:', err);
      toast.error('Failed to delete sponsor');
      throw err;
    }
  };
  
  const addSponsorPayment = async (sponsorId, paymentData) => {
    try {
      const res = await axios.post(`${API}/api/sponsors/${sponsorId}/payments`, paymentData);
      setSponsors(prev => prev.map(s => 
        (s._id || s.id) === sponsorId ? res.data : s
      ));
      toast.success('Payment added');
      return res.data;
    } catch (err) {
      console.error('Failed to add payment:', err);
      toast.error('Failed to add payment');
      throw err;
    }
  };
  
  const removeSponsorPayment = async (sponsorId, paymentId) => {
    try {
      const res = await axios.delete(`${API}/api/sponsors/${sponsorId}/payments/${paymentId}`);
      setSponsors(prev => prev.map(s => 
        (s._id || s.id) === sponsorId ? res.data : s
      ));
      toast.success('Payment removed');
      return res.data;
    } catch (err) {
      console.error('Failed to remove payment:', err);
      toast.error('Failed to remove payment');
      throw err;
    }
  };

  // A3: Toggle entry form
  const toggleEntryForm = () => {
    setShowEntryForm(prev => !prev);
  };

  return (
    <div style={styles.tabContent} data-testid="finance-tab">
      {/* ═══ A1-A3: PANEL HEADER ═══════════════════════════════════════════════ */}
      <div style={styles.panelHeader} data-testid="finance-header">
        {/* A1: Panel Title Block */}
        <AdminPageHeader
          eyebrow="Budget & Payments"
          title="Finance"
          tagline="Command Center — real-time financial health"
        />
        
        {/* Preview Mode Banner - Updated to reflect current backend status */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(198,167,90,0.12) 0%, rgba(198,167,90,0.06) 100%)',
          border: '1px solid rgba(198,167,90,0.25)',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 14 }}>⚡</span>
          <span style={{ fontSize: 13, color: '#8B7355', fontWeight: 500 }}>
            Expenses, Sponsors, Budget & Liquidity: Backend-backed. All finance data is live.
          </span>
        </div>
        
        {/* A2 & A3: Add Finance Entry Button with toggle */}
        <div style={{ display: 'flex', gap: 12 }}>
          {/* Export PDF Button */}
          <button
            style={{
              ...styles.addButton,
              background: 'transparent',
              border: `1px solid ${COLORS.gold}`,
              color: COLORS.gold,
            }}
            onClick={async () => {
              try {
                toast.info('Generating PDF report...');
                const response = await fetch(`${API}/api/finance/export/pdf`);
                if (!response.ok) throw new Error('Export failed');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `finance_report_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('PDF exported successfully');
              } catch (err) {
                console.error('PDF export error:', err);
                toast.error('Failed to export PDF');
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            data-testid="export-pdf-btn-header"
          >
            <Download size={15} />
            Export PDF
          </button>
          
          {/* Add Finance Entry Button */}
          <button
            style={styles.addButton}
            onClick={toggleEntryForm}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
            }}
            data-testid="add-finance-entry-btn"
          >
            {showEntryForm ? (
              <>
                <X size={15} />
                Cancel
              </>
            ) : (
              <>
                <Plus size={15} />
                Add Finance Entry
              </>
            )}
          </button>
        </div>
      </div>

      {/* ═══ B1-B11: GLOBAL KPI STRIP ══════════════════════════════════════════ */}
      <GlobalKPIStrip budget={budget} vendors={vendors} financeEntries={financeEntries} sponsors={sponsors} onBudgetUpdate={(b) => setBudget(b)} />

      {/* Milo Purchases from the Intelligence Notebook (read-only · gated). These ARE real
          finance entries (created via approved write-back) — surfaced with provenance +
          governed undo. Notebook write-back can only CREATE placeholders, never edit
          existing entries / amounts / payments. */}
      {isIntelligenceNotebookEnabled() && (
        <div style={{ margin: '0 0 16px' }} data-testid="finance-milo-purchases">
          <MiloNotebookItems
            kind="finance_entry"
            title="Milo Purchases · from your Notebook"
            emptyText="No purchases created from your Notebook yet."
          />
        </div>
      )}

      {/* Event Pricing Rules removed — now vendor-level */}

      {/* ═══ CONTENT AREA ══════════════════════════════════════════════════════ */}
      <div style={styles.contentArea}>
        {/* ═══ F1-G4: QUICK ENTRY STRIP (Part 3) ═══════════════════════════════ */}
        {showEntryForm && (
          <QuickEntryStrip
            onClose={() => setShowEntryForm(false)}
            onSave={async (entryData, receiptFile) => {
              // Route to correct API based on entry type
              try {
                if (entryData.type === 'sponsor') {
                  // Create SPONSOR via sponsors API
                  await createSponsor({
                    name: entryData.label,
                    type: entryData.category || 'individual',
                    pledge_amount: entryData.contract_amount || 0,
                    amount_received: entryData.amount_paid || 0,
                    notes: entryData.notes,
                  });
                } else {
                  // Create EXPENSE via finance entries API
                  await createFinanceEntry({
                    label: entryData.label,
                    type: 'expense',
                    vendor_name: entryData.vendor_name,
                    contract_amount: entryData.contract_amount || 0,
                    amount_paid: entryData.amount_paid || 0,
                    due_date: entryData.due_date,
                    category: entryData.category,
                    payment_method: entryData.payment_method,
                    status: 'active',
                    needs_review: false,
                    notes: entryData.notes,
                  });
                }
                setShowEntryForm(false);
              } catch (err) {
                // Error already handled in create functions
              }
            }}
          />
        )}

        {/* ═══ C1-E9: TACTICAL DASHBOARD (Part 2) ═══════════════════════════════ */}
        <div style={styles.dashboardGrid} data-testid="tactical-dashboard">
          {/* C1-C13: Action Horizon Card */}
          <ActionHorizonCard 
            vendors={vendors} 
            financeEntries={financeEntries}
            onMarkPaid={async (task) => {
              // Mark as paid via backend API
              if (task.type === 'expense') {
                // Find the original finance entry to get contract_amount
                const entry = financeEntries.find(e => (e._id || e.id) === task.id);
                if (!entry) {
                  toast.error('Entry not found');
                  return;
                }
                
                // Calculate new amount_paid (full payment = contract_amount)
                const newAmountPaid = entry.contract_amount;
                
                // Determine new status based on payment
                const newStatus = newAmountPaid >= entry.contract_amount ? 'paid' : 
                                  newAmountPaid > 0 ? 'partial' : 'unpaid';
                
                try {
                  await updateFinanceEntry(task.id, {
                    amount_paid: newAmountPaid,
                    status: newStatus,
                  });
                  // State already updated by updateFinanceEntry
                } catch (err) {
                  // Error already handled in updateFinanceEntry
                  throw err; // Re-throw to trigger ActionHorizonCard error handling
                }
              } else if (task.type === 'vendor') {
                // Vendor payment - PREVIEW (would need vendor payment endpoint)
                toast.info('Vendor payment tracking coming soon');
              }
            }}
          />
          
          {/* D1-D8: Burn Rate Gauge */}
          <BurnRateGauge budget={budget} vendors={vendors} financeEntries={financeEntries} />
          
          {/* E1-E9: Liquidity Flow Bar */}
          <LiquidityFlowBar 
            liquidity={liquidity} 
            sponsors={sponsors} 
            vendors={vendors}
            financeEntries={financeEntries}
          />
        </div>

        {/* ═══ H1-O10: MASTER LEDGER (Part 4) ═══════════════════════════════════ */}
        <MasterLedger
          expenses={financeEntries.filter(e => e.type === 'expense' || !e.type)}
          sponsors={sponsors}
          orchestrationAwareness={orchestrationAwareness}
          onNavigateToReadiness={onNavigateToReadiness}
          onFlag={async (entry, reason) => {
            // Update entry status via backend API
            try {
              await updateFinanceEntry(entry._id || entry.id, { 
                status: 'flagged', 
                notes: reason ? `Flagged: ${reason}` : entry.notes 
              });
            } catch (err) {
              // Error handled in updateFinanceEntry
            }
          }}
          onPause={async (entry, reason) => {
            // Update entry status via backend API
            try {
              await updateFinanceEntry(entry._id || entry.id, { 
                status: 'paused',
                notes: reason ? `Paused: ${reason}` : entry.notes
              });
            } catch (err) {
              // Error handled in updateFinanceEntry
            }
          }}
          onCancelReplace={async (entry, replacementData) => {
            // POST /api/finance/entries/:id/cancel-replace
            try {
              const response = await axios.post(
                `${API}/api/finance/entries/${entry._id || entry.id}/cancel-replace`,
                {
                  reason: replacementData.reason || 'Cancelled and replaced',
                  create_replacement: replacementData.createReplacement !== false,
                  replacement_label: replacementData.label,
                  replacement_vendor_name: replacementData.vendor_name,
                  replacement_contract_amount: replacementData.contract_amount,
                  replacement_due_date: replacementData.due_date,
                  replacement_category: replacementData.category,
                  replacement_notes: replacementData.notes,
                }
              );
              
              // Update local state: mark original as cancelled, add replacement if created
              setFinanceEntries(prev => {
                const updated = prev.map(e => 
                  (e._id === entry._id || e.id === entry.id) 
                    ? response.data.cancelled_entry 
                    : e
                );
                // Add replacement entry if created
                if (response.data.replacement_entry) {
                  return [response.data.replacement_entry, ...updated];
                }
                return updated;
              });
              
              if (response.data.replacement_entry) {
                toast.success(`Entry cancelled and replacement created`);
              } else {
                toast.success(`Entry cancelled`);
              }
            } catch (err) {
              console.error('Cancel & Replace failed:', err);
              toast.error(err.response?.data?.detail || 'Failed to cancel entry');
            }
          }}
          onAddJournal={async (entry, text) => {
            // Update entry journal via backend API
            const newJournal = [...(entry.journal || []), { 
              id: Date.now().toString(), 
              note: text,
              date: new Date().toISOString() 
            }];
            try {
              await updateFinanceEntry(entry._id || entry.id, { journal: newJournal });
            } catch (err) {
              // Error handled in updateFinanceEntry
            }
          }}
          onRemoveJournal={async (entry, noteId) => {
            // Update entry journal via backend API
            const newJournal = (entry.journal || []).filter(n => 
              n.id !== noteId && n.id !== String(noteId)
            );
            try {
              await updateFinanceEntry(entry._id || entry.id, { journal: newJournal });
            } catch (err) {
              // Error handled in updateFinanceEntry
            }
          }}
          onAddSponsorPayment={async (sponsor, payment) => {
            // Add payment via backend API
            try {
              await addSponsorPayment(sponsor._id || sponsor.id, {
                amount: payment.amount,
                date: payment.date,
                method: payment.method,
                note: payment.note,
              });
            } catch (err) {
              // Error handled in addSponsorPayment
            }
          }}
          onRemoveSponsorPayment={async (sponsor, paymentId) => {
            // Remove payment via backend API
            try {
              await removeSponsorPayment(sponsor._id || sponsor.id, paymentId);
            } catch (err) {
              // Error handled in removeSponsorPayment
            }
          }}
          onEditExpense={(entry) => {
            setEditingExpense(entry);
          }}
          onDeleteExpense={async (entry) => {
            // Delete entry via backend API
            try {
              await deleteFinanceEntry(entry._id || entry.id, entry.label || entry.name);
            } catch (err) {
              // Error handled in deleteFinanceEntry
            }
          }}
          onEditSponsor={(sponsor) => {
            setEditingSponsor(sponsor);
          }}
          onDeleteSponsor={async (sponsor) => {
            // Delete sponsor via backend API
            try {
              await deleteSponsor(sponsor._id || sponsor.id, sponsor.name);
            } catch (err) {
              // Error handled in deleteSponsor
            }
          }}
        />

        {/* ═══ P1-P8: EXPORT ZONE (Part 5) ══════════════════════════════════════ */}
        <ExportZone
          expenses={financeEntries.filter(e => e.type === 'expense' || !e.type)}
          sponsors={sponsors}
          budget={budget}
        />
      </div>

      {/* ═══ EDIT MODALS ═══════════════════════════════════════════════════════ */}
      {editingExpense && (
        <EditExpenseModal
          expense={editingExpense}
          onClose={() => setEditingExpense(null)}
          onSave={async (updatedExpense) => {
            // Update via backend API
            try {
              await updateFinanceEntry(updatedExpense._id || updatedExpense.id, {
                label: updatedExpense.label,
                vendor_name: updatedExpense.vendor_name,
                contract_amount: updatedExpense.contract_amount,
                amount_paid: updatedExpense.amount_paid,
                due_date: updatedExpense.due_date,
                category: updatedExpense.category,
                payment_method: updatedExpense.payment_method,
                status: updatedExpense.status,
                needs_review: updatedExpense.needs_review,
                notes: updatedExpense.notes,
              });
              setEditingExpense(null);
            } catch (err) {
              // Error handled in updateFinanceEntry
            }
          }}
        />
      )}
      
      {editingSponsor && (
        <EditSponsorModal
          sponsor={editingSponsor}
          onClose={() => setEditingSponsor(null)}
          onSave={async (updatedSponsor) => {
            // Update via backend API
            try {
              await updateSponsor(updatedSponsor._id || updatedSponsor.id, {
                name: updatedSponsor.name,
                type: updatedSponsor.type,
                pledge_amount: updatedSponsor.pledge_amount,
                amount_received: updatedSponsor.amount_received,
                expected_date: updatedSponsor.expected_date,
                notes: updatedSponsor.notes,
              });
              setEditingSponsor(null);
            } catch (err) {
              // Error handled in updateSponsor
            }
          }}
        />
      )}

      {/* ═══ Q3 & Q4: SCROLL OVERLAY (when entry form is open) ═════════════════ */}
      {showEntryForm && (
        <div 
          style={{
            ...styles.scrollOverlay,
            // Q4: Animated Overlay Style - opacity synced
            opacity: showEntryForm ? 1 : 0,
            pointerEvents: showEntryForm ? 'auto' : 'none',
          }}
          onClick={() => setShowEntryForm(false)}
          data-testid="scroll-overlay"
        />
      )}

      {/* Q1 & Q2: CSS Animation for Entry Form */}
      <style>{`
        @keyframes entrySlideIn {
          from {
            opacity: 0;
            transform: translateY(-28px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default FinanceTab;
